/**
 * Shared Explanation Generator Service
 *
 * Single source of truth for all human-readable explanations across the platform.
 * Ensures consistent terminology, tone, and framing for:
 *   - Human state labels
 *   - Drift reasons and classifications
 *   - Stability and resilience interpretations
 *   - Confidence explanations
 *   - Biological age insights
 *   - Brain Gym training rationales
 *   - Prediction horizon explanations
 *
 * Voice principles:
 *   - Non-alarming, supportive, and actionable
 *   - Consistent metric names across all modules
 *   - No conflicting explanations between endpoints
 *
 * Extended capabilities:
 *   - Contextual audience/confidence/severity adaptation
 *   - Traceability with contributing factors
 *   - Self-healing consistency validation layer
 *   - Longitudinal narrative summaries (current vs previous period)
 *   - Severity-mapped language calibration
 *   - Multi-format outputs (short / detailed / notification / report)
 *   - Three-layer priority structure (primary / secondary / tertiary)
 *   - Temporal context grounding ("recently", "over the past session")
 *   - Uncertainty language tiers tied to confidence level
 *   - Confidence breakdown (level + reasons)
 *   - Auditability structure (inputs + reasoning path)
 *   - Synthetic scenario testing for edge-case validation
 *   - Localisation-ready translation key structure
 *   - Explanation stability tracking with continuity references
 *   - Confidence-gated explanation depth (high/medium/low layers)
 *   - Pre-generation consistency checks (state/drift/prediction alignment)
 *   - Explanation versioning with version_id in ExplanationAuditRecord
 *   - Risk-level classification with appropriate wording
 *   - Cross-module narrative alignment (Human State / Predictions / Training)
 */

// ─── Contextual Parameters ────────────────────────────────────────────────────

/** Who is receiving the explanation */
export type ExplanationAudience = 'patient' | 'clinician';

/** System certainty level at the time of explanation */
export type ExplanationConfidenceLevel = 'high' | 'medium' | 'low';

/** Severity of the detected change */
export type ExplanationSeverity = 'mild' | 'moderate' | 'significant';

/** Full context bag passed to adaptive explanation builders */
export interface ExplanationContext {
  audience: ExplanationAudience;
  confidenceLevel: ExplanationConfidenceLevel;
  severity: ExplanationSeverity;
}

// ─── Confidence Breakdown ─────────────────────────────────────────────────────

/**
 * Structured confidence breakdown attached to every FormattedExplanation.
 * Makes confidence transparent and interpretable.
 *
 * level  — overall certainty tier
 * reasons — human-readable list of factors driving the level
 */
export interface ConfidenceBreakdown {
  level: 'low' | 'medium' | 'high';
  reasons: string[];
}

/**
 * Derives a ConfidenceBreakdown from signal agreement, data freshness,
 * and stabilityScore.
 *
 * @param signalAgreement  0–1 fraction of signals that agree
 * @param dataFreshnessMs  age of the most recent data point in ms
 * @param stabilityScore   0–100 stability score
 */
export function buildConfidenceBreakdown(params: {
  signalAgreement: number;
  dataFreshnessMs: number;
  stabilityScore: number;
}): ConfidenceBreakdown {
  const { signalAgreement, dataFreshnessMs, stabilityScore } = params;

  const reasons: string[] = [];
  let score = 0; // 0–3 points → maps to low/medium/high

  // Signal agreement
  if (signalAgreement >= 0.75) {
    score += 1;
    reasons.push('Signals are well aligned');
  } else if (signalAgreement >= 0.5) {
    reasons.push('Partial signal agreement');
  } else {
    reasons.push('Limited signal data');
  }

  // Data freshness (stale = >15 min)
  const STALE_THRESHOLD_MS = 15 * 60 * 1000;
  const FRESH_THRESHOLD_MS = 5 * 60 * 1000;
  if (dataFreshnessMs <= FRESH_THRESHOLD_MS) {
    score += 1;
    reasons.push('Data is fresh');
  } else if (dataFreshnessMs <= STALE_THRESHOLD_MS) {
    reasons.push('Data is moderately recent');
  } else {
    reasons.push('Data may be stale');
  }

  // Stability score
  if (stabilityScore >= 70) {
    score += 1;
    reasons.push('Stability score is high');
  } else if (stabilityScore >= 40) {
    reasons.push('Moderate stability');
  } else {
    reasons.push('Recent drift is inconsistent');
  }

  const level: 'low' | 'medium' | 'high' =
    score >= 3 ? 'high' : score >= 2 ? 'medium' : 'low';

  return { level, reasons };
}

// ─── Three-Layer Priority Structure ──────────────────────────────────────────

/**
 * Three-layer explanation structure to reduce cognitive load while preserving depth.
 *
 * primary   — 1-line main takeaway (always shown)
 * secondary — key contributing factors (shown on request)
 * tertiary  — full detailed explanation (expandable)
 */
export interface LayeredExplanation {
  primary: string;
  secondary: string;
  tertiary: string;
}

/**
 * Builds a three-layer explanation from a FormattedExplanation.
 * UI defaults to primary; deeper layers are optional.
 */
export function buildLayeredExplanation(
  formatted: FormattedExplanation,
  factors: string[]
): LayeredExplanation {
  return {
    primary: formatted.short,
    secondary:
      factors.length > 0
        ? `Key factors: ${factors.slice(0, 3).join('; ')}.`
        : formatted.notification,
    tertiary: formatted.detailed,
  };
}

// ─── Temporal Context ─────────────────────────────────────────────────────────

/**
 * Derives a temporal context label from a drift timeline and stability trends.
 *
 * @param driftEventTimestamps  ISO timestamps of recent drift events
 * @param stabilityScores       ordered oldest→newest stability scores
 */
export function buildTemporalContext(params: {
  driftEventTimestamps: string[];
  stabilityScores: number[];
}): string {
  const { driftEventTimestamps, stabilityScores } = params;

  const now = Date.now();
  const SESSION_MS = 2 * 60 * 60 * 1000;   // 2 hours
  const WEEK_MS   = 7 * 24 * 60 * 60 * 1000;

  const recentEvents = driftEventTimestamps.filter(
    (ts) => now - new Date(ts).getTime() < SESSION_MS
  );

  if (recentEvents.length > 0) return 'recently';

  const weekEvents = driftEventTimestamps.filter(
    (ts) => now - new Date(ts).getTime() < WEEK_MS
  );

  if (weekEvents.length > 0) {
    // Check if stability is trending
    if (stabilityScores.length >= 2) {
      const last = stabilityScores[stabilityScores.length - 1];
      const prev = stabilityScores[stabilityScores.length - 2];
      if (last > prev + 5) return 'over the past session';
    }
    return 'over the past week';
  }

  return 'over the past week';
}

/**
 * Injects temporal context into an explanation string.
 * Example: "Your stability has improved [recently]"
 */
export function injectTemporalContext(explanation: string, temporalLabel: string): string {
  // Avoid double-injection
  if (
    explanation.includes('recently') ||
    explanation.includes('over the past') ||
    explanation.includes('over the last')
  ) {
    return explanation;
  }
  return `${explanation} (${temporalLabel})`;
}

// ─── Uncertainty Language Tiers ───────────────────────────────────────────────

/**
 * Returns the appropriate uncertainty prefix for a given confidence level.
 *
 * High   → "You are experiencing…" * Medium →"You appear to be experiencing…" * Low    →"There are indications of…"
 */
export function getUncertaintyPrefix(confidenceLevel: ExplanationConfidenceLevel): string {
  switch (confidenceLevel) {
    case 'high':   return 'You are experiencing';
    case 'medium': return 'You appear to be experiencing';
    case 'low':    return 'There are indications of';
  }
}

/**
 * Wraps a state label with the appropriate uncertainty language tier.
 * Example: buildUncertaintyStatement('Fatigue', 'low') →
 *   "There are indications of fatigue."
 */
export function buildUncertaintyStatement(
  stateConcept: string,
  confidenceLevel: ExplanationConfidenceLevel
): string {
  const prefix = getUncertaintyPrefix(confidenceLevel);
  return `${prefix} ${stateConcept.toLowerCase()}.`;
}

// ─── Severity Language Calibration ───────────────────────────────────────────

/**
 * Maps severity → calibrated wording.
 * Avoids clinical diagnosis language and absolute statements.
 */
export const SEVERITY_WORDING: Record<ExplanationSeverity, string> = {
  mild: 'slight change',
  moderate: 'noticeable change',
  significant: 'clear shift',
};

export function getSeverityWording(severity: ExplanationSeverity): string {
  return SEVERITY_WORDING[severity];
}

// ─── Localisation Keys ────────────────────────────────────────────────────────

/**
 * Translation key registry — separates WHAT to say from HOW to say it.
 * All user-facing strings are keyed here; actual text lives in locale files.
 * This enables future multilingual support without rewriting logic.
 */
export const TRANSLATION_KEYS = {
  // State explanations
  state: {
    Focused: 'explanation.state.focused',
    Fatigued: 'explanation.state.fatigued',
    Stressed: 'explanation.state.stressed',
    Recovering: 'explanation.state.recovering',
    Overloaded: 'explanation.state.overloaded',
    Balanced: 'explanation.state.balanced',
  },
  // Drift
  drift: {
    userMessage: {
      positive: 'explanation.drift.user_message.positive',
      fatigue: 'explanation.drift.user_message.fatigue',
      stress: 'explanation.drift.user_message.stress',
      recovery: 'explanation.drift.user_message.recovery',
      default: 'explanation.drift.user_message.default',
    },
    classification: {
      chronic: 'explanation.drift.classification.chronic',
      acute: 'explanation.drift.classification.acute',
      none: 'explanation.drift.classification.none',
    },
  },
  // Stability / resilience
  stability: {
    high: 'explanation.stability.high',
    moderate: 'explanation.stability.moderate',
    low: 'explanation.stability.low',
  },
  resilience: {
    high: 'explanation.resilience.high',
    moderate: 'explanation.resilience.moderate',
    low: 'explanation.resilience.low',
  },
  // Confidence
  confidence: {
    high: 'explanation.confidence.high',
    moderate: 'explanation.confidence.moderate',
    low: 'explanation.confidence.low',
  },
  // Severity wording
  severity: {
    mild: 'explanation.severity.mild',
    moderate: 'explanation.severity.moderate',
    significant: 'explanation.severity.significant',
  },
  // Longitudinal
  longitudinal: {
    stabilityImproved: 'explanation.longitudinal.stability_improved',
    stabilityDeclined: 'explanation.longitudinal.stability_declined',
    stabilityStable: 'explanation.longitudinal.stability_stable',
    resilienceImproved: 'explanation.longitudinal.resilience_improved',
    resilienceDeclined: 'explanation.longitudinal.resilience_declined',
    consistentRecovery: 'explanation.longitudinal.consistent_recovery',
    driftIncreasing: 'explanation.longitudinal.drift_increasing',
    driftDecreasing: 'explanation.longitudinal.drift_decreasing',
  },
  // Multi-format
  format: {
    shortSuffix: 'explanation.format.short_suffix',
    notificationPrefix: 'explanation.format.notification_prefix',
  },
} as const;

// ─── Multi-Format Output ──────────────────────────────────────────────────────

/** Structured multi-format explanation output */
export interface FormattedExplanation {
  /** One-line summary for UI cards, badges, and list items */
  short: string;
  /** Full expanded explanation for detail views */
  detailed: string;
  /** Concise push notification copy (≤80 chars) */
  notification: string;
  /** Report-ready paragraph with clinical/formal tone */
  report: string;
  /** Contributing factors for traceability */
  factors: string[];
  /** Translation key for the primary message (i18n) */
  translationKey: string;
  /**
   * Confidence breakdown — makes certainty transparent and interpretable.
   * Populated when signal agreement, data freshness, and stabilityScore are available.
   */
  confidence_explanation?: ConfidenceBreakdown;
  /**
   * Three-layer priority structure for progressive disclosure.
   * UI defaults to primary; secondary and tertiary are optional/expandable.
   */
  layers?: LayeredExplanation;
}

// ─── Traceability ─────────────────────────────────────────────────────────────

/** A single contributing factor with its metric and direction */
export interface ContributingFactor {
  metric: string;
  direction: 'increase' | 'decrease' | 'stable';
  magnitude?: number; // percentage change
}

/**
 * Converts a list of contributing factors into human-readable strings.
 * Example: "Based on reduced HRV and slower reaction time"
 */
export function buildFactorDescriptions(factors: ContributingFactor[]): string[] {
  return factors.map((f) => {
    const label = getMetricLabel(f.metric);
    if (f.direction === 'decrease') {
      return f.magnitude != null
        ? `${label} decrease (${f.magnitude.toFixed(0)}%)`
        : `${label} decrease`;
    }
    if (f.direction === 'increase') {
      return f.magnitude != null
        ? `${label} increase (${f.magnitude.toFixed(0)}%)`
        : `${label} increase`;
    }
    return `${label} stable`;
  });
}

/**
 * Builds a natural-language preamble from contributing factors.
 * Example: "Based on reduced HRV and slower reaction time"
 */
export function buildFactorPreamble(factors: ContributingFactor[]): string {
  if (factors.length === 0) return '';
  const parts = factors.slice(0, 3).map((f) => {
    const label = getMetricLabel(f.metric);
    if (f.direction === 'decrease') return `reduced ${label}`;
    if (f.direction === 'increase') return `elevated ${label}`;
    return label;
  });
  if (parts.length === 1) return `Based on ${parts[0]}`;
  const last = parts.pop();
  return `Based on ${parts.join(', ')} and ${last}`;
}

// ─── Explanation Stability Tracking ──────────────────────────────────────────

/**
 * A snapshot of a previously generated explanation for continuity comparison.
 */
export interface ExplanationSnapshot {
  version_id: string;
  generatedAt: string;
  stateLabel: string;
  driftType?: string;
  shortText: string;
  detailedText: string;
  reasoningPath: string[];
}

/**
 * Result of comparing the current explanation against previous snapshots.
 */
export interface StabilityCheckResult {
  /** Whether a sudden wording shift was detected */
  suddenShiftDetected: boolean;
  /** Whether conflicting narratives were found across recent explanations */
  conflictingNarrativeDetected: boolean;
  /** Continuity reference to prepend when instability is detected */
  continuityReference?: string;
  /** Smoothed explanation text (if shift detected) */
  smoothedExplanation?: string;
  /** Severity of instability: none / minor / major */
  instabilitySeverity: 'none' | 'minor' | 'major';
}

/**
 * In-memory ring buffer of recent explanation snapshots per user (last 10).
 * Key: userId, Value: ordered array of snapshots (oldest first).
 */
const explanationHistory = new Map<string, ExplanationSnapshot[]>();
const MAX_HISTORY = 10;

/**
 * Records a new explanation snapshot for a user.
 */
export function recordExplanationSnapshot(userId: string, snapshot: ExplanationSnapshot): void {
  const history = explanationHistory.get(userId) ?? [];
  history.push(snapshot);
  if (history.length > MAX_HISTORY) history.shift();
  explanationHistory.set(userId, history);
}

/**
 * Returns the recent explanation history for a user.
 */
export function getExplanationHistory(userId: string): ExplanationSnapshot[] {
  return explanationHistory.get(userId) ?? [];
}

/**
 * Compares the current explanation against recent history to detect:
 *   - Sudden wording shifts (state label changed without drift evidence)
 *   - Conflicting narratives (e.g. "Focused" followed immediately by "Fatigued")
 *
 * When instability is detected:
 *   - Smooths explanation language
 *   - Adds continuity reference: "This continues the pattern observed earlier"
 */
export function checkExplanationStability(params: {
  userId: string;
  currentStateLabel: string;
  currentDriftType?: string;
  currentShortText: string;
  currentDetailedText: string;
}): StabilityCheckResult {
  const { userId, currentStateLabel, currentDriftType, currentShortText, currentDetailedText } = params;
  const history = explanationHistory.get(userId) ?? [];

  if (history.length === 0) {
    return { suddenShiftDetected: false, conflictingNarrativeDetected: false, instabilitySeverity: 'none' };
  }

  const POSITIVE_STATES = ['Focused', 'Balanced', 'Recovering'];
  const NEGATIVE_STATES = ['Fatigued', 'Stressed', 'Overloaded'];

  const recent = history.slice(-3); // last 3 snapshots
  const lastSnapshot = recent[recent.length - 1];

  // Detect sudden wording shift: state polarity flipped without drift evidence
  const lastPositive = POSITIVE_STATES.includes(lastSnapshot.stateLabel);
  const currentPositive = POSITIVE_STATES.includes(currentStateLabel);
  const lastNegative = NEGATIVE_STATES.includes(lastSnapshot.stateLabel);
  const currentNegative = NEGATIVE_STATES.includes(currentStateLabel);

  const polarityFlipped =
    (lastPositive && currentNegative) || (lastNegative && currentPositive);
  const noDriftEvidence = !currentDriftType || currentDriftType === 'neutral';
  const suddenShiftDetected = polarityFlipped && noDriftEvidence;

  // Detect conflicting narratives: oscillation between opposite states in last 3
  let conflictingNarrativeDetected = false;
  if (recent.length >= 2) {
    const stateLabels = recent.map(s => s.stateLabel).concat(currentStateLabel);
    const positiveCount = stateLabels.filter(s => POSITIVE_STATES.includes(s)).length;
    const negativeCount = stateLabels.filter(s => NEGATIVE_STATES.includes(s)).length;
    // Oscillation: both positive and negative states present in last 3+current
    conflictingNarrativeDetected = positiveCount >= 1 && negativeCount >= 1 && stateLabels.length >= 3;
  }

  if (!suddenShiftDetected && !conflictingNarrativeDetected) {
    return { suddenShiftDetected: false, conflictingNarrativeDetected: false, instabilitySeverity: 'none' };
  }

  const instabilitySeverity: 'minor' | 'major' =
    suddenShiftDetected && conflictingNarrativeDetected ? 'major' : 'minor';

  // Build continuity reference
  const prevStateLabel = lastSnapshot.stateLabel;
  let continuityReference: string;
  if (conflictingNarrativeDetected) {
    continuityReference = `This continues the pattern observed earlier — your state has been variable between ${prevStateLabel} and ${currentStateLabel}.`;
  } else {
    continuityReference = `This continues the pattern observed earlier — a shift from ${prevStateLabel} to ${currentStateLabel} has been noted.`;
  }

  // Smooth explanation: prepend continuity reference
  const smoothedExplanation = `${continuityReference} ${currentDetailedText}`;

  return {
    suddenShiftDetected,
    conflictingNarrativeDetected,
    continuityReference,
    smoothedExplanation,
    instabilitySeverity,
  };
}

// ─── Risk-Level Classification ────────────────────────────────────────────────

/**
 * Risk level for an explanation.
 *   low    → general insights
 *   medium → behavioural patterns
 *   high   → health-related implications
 */
export type ExplanationRiskLevel = 'low' | 'medium' | 'high';

/**
 * Classifies the risk level of an explanation based on state, drift, and severity.
 */
export function classifyExplanationRiskLevel(params: {
  stateLabel: HumanStateLabel;
  driftClassification?: DriftClassification;
  severity?: ExplanationSeverity;
  anomalyLevel?: 'none' | 'mild' | 'moderate' | 'critical';
}): ExplanationRiskLevel {
  const { stateLabel, driftClassification, severity, anomalyLevel } = params;

  // Critical anomaly or significant severity with negative state → high risk
  if (
    anomalyLevel === 'critical' ||
    (severity === 'significant' && (stateLabel === 'Overloaded' || stateLabel === 'Stressed'))
  ) {
    return 'high';
  }

  // Chronic drift or moderate anomaly or Overloaded/Stressed state → medium risk
  if (
    driftClassification === 'chronic' ||
    anomalyLevel === 'moderate' ||
    stateLabel === 'Overloaded' ||
    stateLabel === 'Stressed' ||
    stateLabel === 'Fatigued'
  ) {
    return 'medium';
  }

  return 'low';
}

/**
 * Applies risk-level-appropriate wording adjustments to an explanation.
 *   low    → general, informational tone
 *   medium → behavioural pattern framing
 *   high   → stronger uncertainty language, clearer guidance
 */
export function applyRiskLevelWording(
  explanation: string,
  riskLevel: ExplanationRiskLevel,
  audience: ExplanationAudience = 'patient'
): string {
  switch (riskLevel) {
    case 'high':
      if (audience === 'clinician') {
        return `[HIGH RISK] ${explanation} Clinical review is recommended.`;
      }
      return `${explanation} This may have health-related implications — consider speaking with your care team if this persists.`;
    case 'medium':
      if (audience === 'clinician') {
        return `[MEDIUM RISK] ${explanation} Monitor for sustained patterns.`;
      }
      return `${explanation} This may reflect a behavioural pattern worth monitoring.`;
    case 'low':
    default:
      return explanation;
  }
}

// ─── Pre-Generation Consistency Checks ───────────────────────────────────────

/**
 * Input alignment check performed BEFORE explanation generation.
 * Validates:
 *   - state vs drift alignment
 *   - stability vs resilience coherence
 *   - prediction vs confidence
 *
 * Returns adjusted inputs when misalignments are detected, reducing reliance
 * on post-generation self-healing.
 */
export interface PreGenerationCheckResult {
  /** Whether any adjustments were made */
  adjusted: boolean;
  /** List of adjustments applied */
  adjustments: string[];
  /** Adjusted context (may differ from input if misalignments found) */
  adjustedContext: ExplanationContext;
  /** Adjusted severity (may be softened/strengthened) */
  adjustedSeverity: ExplanationSeverity;
  /** Adjusted confidence level */
  adjustedConfidenceLevel: ExplanationConfidenceLevel;
}

export function runPreGenerationChecks(params: {
  stateLabel: HumanStateLabel;
  driftType?: DriftType;
  driftClassification?: DriftClassification;
  stabilityScore?: number;
  resilienceScore?: number;
  confidence?: number;
  predictedPerformance?: number;
  context: ExplanationContext;
}): PreGenerationCheckResult {
  const {
    stateLabel,
    driftType,
    driftClassification,
    stabilityScore,
    resilienceScore,
    confidence,
    predictedPerformance,
    context,
  } = params;

  const adjustments: string[] = [];
  let adjustedSeverity = context.severity;
  let adjustedConfidenceLevel = context.confidenceLevel;

  // 1. State vs drift alignment: positive drift → soften severity if currently significant
  if (driftType === 'positive' && adjustedSeverity === 'significant') {
    adjustedSeverity = 'moderate';
    adjustments.push('Severity softened from significant to moderate due to positive drift.');
  }

  // 2. Stability vs resilience coherence: high stability + high resilience → boost confidence
  if (
    stabilityScore != null && stabilityScore >= 75 &&
    resilienceScore != null && resilienceScore >= 70 &&
    adjustedConfidenceLevel === 'low'
  ) {
    adjustedConfidenceLevel = 'medium';
    adjustments.push('Confidence level raised from low to medium due to high stability and resilience.');
  }

  // 3. Prediction vs confidence: low confidence → cap severity at moderate
  if (confidence != null && confidence < 0.4 && adjustedSeverity === 'significant') {
    adjustedSeverity = 'moderate';
    adjustments.push('Severity capped at moderate due to low prediction confidence.');
  }

  // 4. Chronic drift → ensure severity is at least moderate
  if (driftClassification === 'chronic' && adjustedSeverity === 'mild') {
    adjustedSeverity = 'moderate';
    adjustments.push('Severity raised from mild to moderate due to chronic drift classification.');
  }

  // 5. Overloaded/Stressed state → ensure confidence level is not overstated
  if (
    (stateLabel === 'Overloaded' || stateLabel === 'Stressed') &&
    adjustedConfidenceLevel === 'high' &&
    (confidence == null || confidence < 0.7)
  ) {
    adjustedConfidenceLevel = 'medium';
    adjustments.push('Confidence level reduced for Overloaded/Stressed state without sufficient signal confidence.');
  }

  // 6. High predicted performance with negative state → reduce confidence level
  if (
    predictedPerformance != null && predictedPerformance > 80 &&
    (stateLabel === 'Fatigued' || stateLabel === 'Overloaded') &&
    adjustedConfidenceLevel === 'high'
  ) {
    adjustedConfidenceLevel = 'medium';
    adjustments.push('Confidence level reduced: high predicted performance conflicts with negative state label.');
  }

  const adjustedContext: ExplanationContext = {
    ...context,
    severity: adjustedSeverity,
    confidenceLevel: adjustedConfidenceLevel,
  };

  return {
    adjusted: adjustments.length > 0,
    adjustments,
    adjustedContext,
    adjustedSeverity,
    adjustedConfidenceLevel,
  };
}

// ─── Explanation Versioning ───────────────────────────────────────────────────

/**
 * Generates a unique version_id for an explanation.
 * Format: exp_<timestamp>_<random>
 */
export function generateExplanationVersionId(): string {
  return `exp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

// ─── Cross-Module Narrative Alignment ────────────────────────────────────────

/**
 * Session-level narrative registry.
 * Stores the active narrative for each module in a session to prevent conflicts.
 * Key: sessionId, Value: module narratives
 */
export interface SessionNarrativeEntry {
  module: 'human_state' | 'prediction' | 'training';
  stateLabel: string;
  driftType?: string;
  shortText: string;
  generatedAt: string;
}

const sessionNarratives = new Map<string, SessionNarrativeEntry[]>();

/**
 * Registers a module's narrative for a session.
 * Replaces any existing entry for the same module in the session.
 */
export function registerSessionNarrative(
  sessionId: string,
  entry: SessionNarrativeEntry
): void {
  const entries = sessionNarratives.get(sessionId) ?? [];
  const filtered = entries.filter(e => e.module !== entry.module);
  filtered.push(entry);
  sessionNarratives.set(sessionId, filtered);
}

/**
 * Checks for conflicting messages across modules in the same session.
 * Returns a list of conflicts and a unified narrative suggestion.
 */
export interface NarrativeAlignmentResult {
  hasConflicts: boolean;
  conflicts: string[];
  unifiedNarrative?: string;
  alignedModules: string[];
}

export function checkCrossModuleAlignment(sessionId: string): NarrativeAlignmentResult {
  const entries = sessionNarratives.get(sessionId) ?? [];
  if (entries.length < 2) {
    return { hasConflicts: false, conflicts: [], alignedModules: entries.map(e => e.module) };
  }

  const POSITIVE_STATES = ['Focused', 'Balanced', 'Recovering'];
  const NEGATIVE_STATES = ['Fatigued', 'Stressed', 'Overloaded'];

  const conflicts: string[] = [];
  const alignedModules: string[] = [];

  // Check each pair of modules for conflicting state polarity
  for (let i = 0; i < entries.length; i++) {
    for (let j = i + 1; j < entries.length; j++) {
      const a = entries[i];
      const b = entries[j];
      const aPositive = POSITIVE_STATES.includes(a.stateLabel);
      const bPositive = POSITIVE_STATES.includes(b.stateLabel);
      const aNegative = NEGATIVE_STATES.includes(a.stateLabel);
      const bNegative = NEGATIVE_STATES.includes(b.stateLabel);

      if ((aPositive && bNegative) || (aNegative && bPositive)) {
        conflicts.push(
          `Conflicting narratives: ${a.module} reports "${a.stateLabel}" but ${b.module} reports "${b.stateLabel}".`
        );
      } else {
        if (!alignedModules.includes(a.module)) alignedModules.push(a.module);
        if (!alignedModules.includes(b.module)) alignedModules.push(b.module);
      }
    }
  }

  // Build unified narrative from the most recent dominant state
  let unifiedNarrative: string | undefined;
  if (conflicts.length > 0) {
    // Use the state with the most module agreement
    const stateCounts: Record<string, number> = {};
    for (const e of entries) {
      stateCounts[e.stateLabel] = (stateCounts[e.stateLabel] ?? 0) + 1;
    }
    const dominantState = Object.entries(stateCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'Balanced';
    unifiedNarrative = `The overall system assessment indicates a "${dominantState}" state. Some module-level variation exists — the primary assessment takes precedence.`;
  }

  return {
    hasConflicts: conflicts.length > 0,
    conflicts,
    unifiedNarrative,
    alignedModules,
  };
}

/**
 * Clears session narratives (call on session end or after TTL).
 */
export function clearSessionNarratives(sessionId: string): void {
  sessionNarratives.delete(sessionId);
}

// ─── Module Priority Hierarchy ────────────────────────────────────────────────

/**
 * Priority hierarchy for cross-module conflict resolution.
 * Human State > Predictions > Training
 * Higher number = higher priority.
 */
export const MODULE_PRIORITY: Record<SessionNarrativeEntry['module'], number> = {
  human_state: 3,
  prediction: 2,
  training: 1,
};

export interface ModuleOverrideRecord {
  id: string;
  sessionId: string;
  timestamp: string;
  /** The module whose narrative was overridden */
  overriddenModule: SessionNarrativeEntry['module'];
  /** The module that determined narrative direction */
  dominantModule: SessionNarrativeEntry['module'];
  /** Why the override occurred */
  reason: string;
  /** Original state label of the overridden module */
  originalStateLabel: string;
  /** Adjusted state label after override */
  adjustedStateLabel: string;
}

/** In-memory override log (capped) */
const moduleOverrideLog: ModuleOverrideRecord[] = [];
const MAX_OVERRIDE_LOG = 500;

function pushOverrideRecord(record: ModuleOverrideRecord): void {
  moduleOverrideLog.push(record);
  if (moduleOverrideLog.length > MAX_OVERRIDE_LOG) moduleOverrideLog.shift();
}

/**
 * Resolves conflicts between module narratives using the priority hierarchy.
 * Human State > Predictions > Training.
 *
 * When a conflict is detected:
 *  - The higher-priority module determines narrative direction
 *  - Lower-priority modules receive adjusted explanations
 *  - Override is logged with reason
 *
 * Returns the resolved entries and any override records generated.
 */
export function resolveNarrativeConflicts(
  sessionId: string
): {
  resolvedEntries: SessionNarrativeEntry[];
  overrides: ModuleOverrideRecord[];
  hasConflicts: boolean;
  unifiedNarrative: string;
} {
  const entries = sessionNarratives.get(sessionId) ?? [];
  if (entries.length < 2) {
    return {
      resolvedEntries: entries,
      overrides: [],
      hasConflicts: false,
      unifiedNarrative: entries[0]?.shortText ?? '',
    };
  }

  const POSITIVE_STATES = ['Focused', 'Balanced', 'Recovering'];
  const NEGATIVE_STATES = ['Fatigued', 'Stressed', 'Overloaded'];

  // Sort by priority descending — highest priority first
  const sorted = [...entries].sort(
    (a, b) => (MODULE_PRIORITY[b.module] ?? 0) - (MODULE_PRIORITY[a.module] ?? 0)
  );

  const dominant = sorted[0]; // highest priority module
  const dominantPositive = POSITIVE_STATES.includes(dominant.stateLabel);
  const dominantNegative = NEGATIVE_STATES.includes(dominant.stateLabel);

  const overrides: ModuleOverrideRecord[] = [];
  const resolvedEntries: SessionNarrativeEntry[] = [dominant];

  for (let i = 1; i < sorted.length; i++) {
    const entry = sorted[i];
    const entryPositive = POSITIVE_STATES.includes(entry.stateLabel);
    const entryNegative = NEGATIVE_STATES.includes(entry.stateLabel);

    const hasConflict =
      (dominantPositive && entryNegative) || (dominantNegative && entryPositive);

    if (hasConflict) {
      // Adjust lower-priority module to align with dominant
      const adjustedLabel = dominantPositive
        ? (entry.stateLabel === 'Fatigued' ? 'Recovering' : 'Balanced')
        : (entry.stateLabel === 'Focused' ? 'Balanced' : entry.stateLabel);

      const reason =
        `${dominant.module} (priority ${MODULE_PRIORITY[dominant.module]}) reports "${dominant.stateLabel}" ` +
        `which conflicts with ${entry.module} (priority ${MODULE_PRIORITY[entry.module]}) reporting "${entry.stateLabel}". ` +
        `Higher-priority module determines narrative direction.`;

      const overrideRecord: ModuleOverrideRecord = {
        id: `ovr_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        sessionId,
        timestamp: new Date().toISOString(),
        overriddenModule: entry.module,
        dominantModule: dominant.module,
        reason,
        originalStateLabel: entry.stateLabel,
        adjustedStateLabel: adjustedLabel,
      };
      pushOverrideRecord(overrideRecord);
      overrides.push(overrideRecord);

      resolvedEntries.push({
        ...entry,
        stateLabel: adjustedLabel,
        shortText: `${entry.shortText} (aligned with ${dominant.module} assessment)`,
      });
    } else {
      resolvedEntries.push(entry);
    }
  }

  // Update session narratives with resolved entries
  sessionNarratives.set(sessionId, resolvedEntries);

  let unifiedNarrative =
    overrides.length > 0
      ? `The ${dominant.module.replace('_', ' ')} assessment ("${dominant.stateLabel}") takes precedence. ` +
        `${overrides.length} module narrative(s) adjusted for consistency.`
      : `All modules are aligned around "${dominant.stateLabel}".`;

  return {
    resolvedEntries,
    overrides,
    hasConflicts: overrides.length > 0,
    unifiedNarrative,
  };
}

/**
 * Returns the module override log, optionally filtered by session or module.
 */
export function getModuleOverrideLog(options?: {
  sessionId?: string;
  overriddenModule?: SessionNarrativeEntry['module'];
  fromDate?: string;
}): ModuleOverrideRecord[] {
  return moduleOverrideLog.filter(r => {
    if (options?.sessionId && r.sessionId !== options.sessionId) return false;
    if (options?.overriddenModule && r.overriddenModule !== options.overriddenModule) return false;
    if (options?.fromDate && r.timestamp < options.fromDate) return false;
    return true;
  });
}

// ─── Semantic Explanation Drift Detection ─────────────────────────────────────

/**
 * A snapshot of an explanation's semantic fingerprint.
 * Used to detect gradual shifts in interpretation over time.
 */
export interface ExplanationSemanticSnapshot {
  version_id: string;
  timestamp: string;
  stateLabel: string;
  driftType?: string;
  /** Key causal factors mentioned in the explanation */
  causalFactors: string[];
  /** Semantic polarity: positive / negative / neutral */
  polarity: 'positive' | 'negative' | 'neutral';
  /** Dominant theme keywords */
  themeKeywords: string[];
}

/**
 * Result of a semantic drift detection check.
 */
export interface SemanticDriftResult {
  driftDetected: boolean;
  /** Type of drift found */
  driftType: 'gradual_shift' | 'causal_factor_change' | 'polarity_flip' | 'none';
  /** Human-readable description of the drift */
  description: string;
  /** Message to surface to the user if drift is significant */
  userMessage?: string;
  /** Number of snapshots compared */
  snapshotsCompared: number;
}

/** Per-user semantic snapshot history (last 10 per user) */
const semanticSnapshotHistory = new Map<string, ExplanationSemanticSnapshot[]>();
const MAX_SEMANTIC_SNAPSHOTS = 10;

/**
 * Extracts a semantic fingerprint from an explanation text and state context.
 */
function extractSemanticFingerprint(params: {
  stateLabel: string;
  driftType?: string;
  explanationText: string;
}): Pick<ExplanationSemanticSnapshot, 'causalFactors' | 'polarity' | 'themeKeywords'> {
  const text = params.explanationText.toLowerCase();

  // Causal factor extraction — look for known signal keywords
  const CAUSAL_KEYWORDS = [
    'hrv', 'heart rate', 'sleep', 'fatigue', 'stress', 'focus', 'attention',
    'reaction', 'energy', 'recovery', 'cognitive load', 'drift', 'stability',
    'resilience', 'overload', 'decline', 'improvement', 'variability',
  ];
  const causalFactors = CAUSAL_KEYWORDS.filter(kw => text.includes(kw));

  // Polarity from state label
  const POSITIVE_STATES = ['focused', 'balanced', 'recovering'];
  const NEGATIVE_STATES = ['fatigued', 'stressed', 'overloaded'];
  const stateL = params.stateLabel.toLowerCase();
  const polarity: 'positive' | 'negative' | 'neutral' = POSITIVE_STATES.includes(stateL) ?'positive' : NEGATIVE_STATES.includes(stateL) ?'negative' : 'neutral';

  // Theme keywords — top 5 most specific terms
  const THEME_KEYWORDS = [
    'rest', 'recovery', 'peak', 'decline', 'sustained', 'acute', 'chronic',
    'high-performance', 'reduced', 'elevated', 'optimal', 'critical',
  ];
  const themeKeywords = THEME_KEYWORDS.filter(kw => text.includes(kw));

  return { causalFactors, polarity, themeKeywords };
}

/**
 * Records a semantic snapshot for a user's explanation.
 * Call after each explanation generation.
 */
export function recordSemanticSnapshot(
  userId: string,
  params: {
    version_id: string;
    stateLabel: string;
    driftType?: string;
    explanationText: string;
  }
): void {
  const fingerprint = extractSemanticFingerprint(params);
  const snapshot: ExplanationSemanticSnapshot = {
    version_id: params.version_id,
    timestamp: new Date().toISOString(),
    stateLabel: params.stateLabel,
    driftType: params.driftType,
    ...fingerprint,
  };

  const history = semanticSnapshotHistory.get(userId) ?? [];
  history.push(snapshot);
  if (history.length > MAX_SEMANTIC_SNAPSHOTS) history.shift();
  semanticSnapshotHistory.set(userId, history);
}

/**
 * Detects semantic drift by comparing the current explanation against
 * recent snapshots. Looks for:
 *  - Gradual shift in interpretation (polarity trend)
 *  - Change in causal factors emphasis
 *
 * Returns a SemanticDriftResult with user-facing message if drift is detected.
 */
export function detectSemanticDrift(
  userId: string,
  currentSnapshot: Pick<ExplanationSemanticSnapshot, 'stateLabel' | 'driftType' | 'causalFactors' | 'polarity' | 'themeKeywords'>
): SemanticDriftResult {
  const history = semanticSnapshotHistory.get(userId) ?? [];
  if (history.length < 3) {
    return { driftDetected: false, driftType: 'none', description: 'Insufficient history for drift detection.', snapshotsCompared: history.length };
  }

  // Compare against last 3 snapshots
  const recent = history.slice(-3);

  // 1. Polarity flip detection — current polarity differs from majority of recent
  const recentPolarities = recent.map(s => s.polarity);
  const polarityCount: Record<string, number> = {};
  for (const p of recentPolarities) polarityCount[p] = (polarityCount[p] ?? 0) + 1;
  const dominantPolarity = Object.entries(polarityCount).sort((a, b) => b[1] - a[1])[0]?.[0];
  if (dominantPolarity && dominantPolarity !== currentSnapshot.polarity && polarityCount[dominantPolarity] >= 2) {
    return {
      driftDetected: true,
      driftType: 'polarity_flip',
      description: `Interpretation polarity shifted from "${dominantPolarity}" to "${currentSnapshot.polarity}".`,
      userMessage: 'Your pattern interpretation has evolved based on new data.',
      snapshotsCompared: recent.length,
    };
  }

  // 2. Causal factor change — significant change in which factors are emphasised
  const recentFactorSets = recent.map(s => new Set(s.causalFactors));
  const allRecentFactors = new Set(recent.flatMap(s => s.causalFactors));
  const currentFactorSet = new Set(currentSnapshot.causalFactors);

  // Jaccard similarity between current and union of recent
  const intersection = [...currentFactorSet].filter(f => allRecentFactors.has(f)).length;
  const union = new Set([...currentFactorSet, ...allRecentFactors]).size;
  const jaccardSimilarity = union > 0 ? intersection / union : 1;

  if (jaccardSimilarity < 0.3 && currentSnapshot.causalFactors.length > 0) {
    const newFactors = [...currentFactorSet].filter(f => !allRecentFactors.has(f));
    return {
      driftDetected: true,
      driftType: 'causal_factor_change',
      description: `Causal factor emphasis has shifted. New factors: ${newFactors.join(', ')}.`,
      userMessage: 'Your pattern interpretation has evolved based on new data.',
      snapshotsCompared: recent.length,
    };
  }

  // 3. Gradual shift — polarity has been trending in one direction across all snapshots
  if (recent.length >= 3) {
    const polarityValues = recent.map(s =>
      s.polarity === 'positive' ? 1 : s.polarity === 'negative' ? -1 : 0
    );
    const currentPolarityValue = currentSnapshot.polarity === 'positive' ? 1 : currentSnapshot.polarity === 'negative' ? -1 : 0;
    const allValues = [...polarityValues, currentPolarityValue];
    // Check monotonic trend
    const isMonotonicallyDecreasing = allValues.every((v, i) => i === 0 || v <= allValues[i - 1]);
    const isMonotonicallyIncreasing = allValues.every((v, i) => i === 0 || v >= allValues[i - 1]);
    if ((isMonotonicallyDecreasing || isMonotonicallyIncreasing) && allValues[0] !== allValues[allValues.length - 1]) {
      const direction = isMonotonicallyDecreasing ? 'declining' : 'improving';
      return {
        driftDetected: true,
        driftType: 'gradual_shift',
        description: `Gradual ${direction} trend detected across ${allValues.length} snapshots.`,
        userMessage: 'Your pattern interpretation has evolved based on new data.',
        snapshotsCompared: recent.length,
      };
    }
  }

  return {
    driftDetected: false,
    driftType: 'none',
    description: 'No semantic drift detected.',
    snapshotsCompared: recent.length,
  };
}

// ─── Feedback-Driven Explanation Refinement ───────────────────────────────────

/**
 * Applies feedback-driven refinement to an explanation.
 * If a user has low clarity ratings → simplify.
 * If a clinician has overridden repeatedly → adjust template weighting.
 *
 * Returns the refined explanation text and the adjustments applied.
 */
export function applyFeedbackRefinement(params: {
  explanationText: string;
  layers: LayeredExplanation;
  complexityAdjustment: -1 | 0 | 1;
  preferredStyle: 'concise' | 'standard' | 'detailed';
  clinicianOverrideCount?: number;
}): {
  refinedText: string;
  refinedLayers: LayeredExplanation;
  adjustmentsApplied: string[];
} {
  const { explanationText, layers, complexityAdjustment, preferredStyle, clinicianOverrideCount = 0 } = params;
  const adjustmentsApplied: string[] = [];
  let refinedText = explanationText;
  let refinedLayers = { ...layers };

  // Low clarity → simplify
  if (complexityAdjustment === -1 || preferredStyle === 'concise') {
    // Truncate to primary layer only
    refinedText = layers.primary;
    refinedLayers = {
      primary: layers.primary,
      secondary: '',
      tertiary: '',
    };
    adjustmentsApplied.push('Simplified to primary layer due to low clarity feedback.');
  }

  // Expert user → allow deeper layers
  if (complexityAdjustment === 1 || preferredStyle === 'detailed') {
    refinedText = `${layers.primary} ${layers.secondary} ${layers.tertiary}`.trim();
    adjustmentsApplied.push('Expanded to full detail due to expert user preference.');
  }

  // Clinician override adjustment — soften certainty language if repeated overrides
  if (clinicianOverrideCount >= 3) {
    refinedText = refinedText
      .replace(/\bYou are experiencing\b/g, 'You appear to be experiencing')
      .replace(/\bimmediately\b/g, 'soon')
      .replace(/\bcritically\b/g, 'notably');
    adjustmentsApplied.push(`Softened certainty language due to ${clinicianOverrideCount} clinician overrides.`);
  }

  return { refinedText, refinedLayers, adjustmentsApplied };
}

// ─── Risk-Level Consistency Enforcement ──────────────────────────────────────

/**
 * Enforces risk-level consistency across modules.
 * If one module outputs high risk, all related modules must reflect compatible tone.
 *
 * Prevents: high-risk state + low-risk prediction wording.
 * Aligns: uncertainty language and guidance strength.
 */
export interface RiskConsistencyResult {
  consistent: boolean;
  adjustments: string[];
  /** Aligned risk level to use across all modules */
  alignedRiskLevel: 'low' | 'medium' | 'high';
  /** Aligned uncertainty prefix */
  alignedUncertaintyPrefix: string;
  /** Aligned guidance strength */
  guidanceStrength: 'gentle' | 'moderate' | 'strong';
}

export function enforceRiskLevelConsistency(params: {
  humanStateRisk: 'low' | 'medium' | 'high';
  predictionRisk?: 'low' | 'medium' | 'high';
  trainingRisk?: 'low' | 'medium' | 'high';
}): RiskConsistencyResult {
  const { humanStateRisk, predictionRisk, trainingRisk } = params;
  const adjustments: string[] = [];

  // Human State has highest priority — it determines the floor
  const riskLevels = [humanStateRisk, predictionRisk, trainingRisk].filter(Boolean) as ('low' | 'medium' | 'high')[];
  const riskOrder: Record<string, number> = { low: 1, medium: 2, high: 3 };

  // Aligned risk = maximum risk level (highest risk wins to prevent under-reporting)
  const maxRiskNum = Math.max(...riskLevels.map(r => riskOrder[r] ?? 1));
  const alignedRiskLevel: 'low' | 'medium' | 'high' =
    maxRiskNum >= 3 ? 'high' : maxRiskNum >= 2 ? 'medium' : 'low';

  // Check for inconsistencies
  if (humanStateRisk === 'high' && (predictionRisk === 'low' || trainingRisk === 'low')) {
    adjustments.push('Elevated prediction/training risk language to match high-risk human state.');
  }
  if (humanStateRisk === 'low' && predictionRisk === 'high') {
    adjustments.push('Prediction risk elevated above human state — aligned to medium.');
  }

  // Uncertainty language aligned to risk level
  const alignedUncertaintyPrefix =
    alignedRiskLevel === 'high' ? 'You are experiencing'
    : alignedRiskLevel === 'medium'? 'You appear to be experiencing' :'There are indications of';

  // Guidance strength
  const guidanceStrength: 'gentle' | 'moderate' | 'strong' =
    alignedRiskLevel === 'high' ? 'strong'
    : alignedRiskLevel === 'medium' ? 'moderate' : 'gentle';

  return {
    consistent: adjustments.length === 0,
    adjustments,
    alignedRiskLevel,
    alignedUncertaintyPrefix,
    guidanceStrength,
  };
}

// ─── Session Narrative Memory ─────────────────────────────────────────────────

/**
 * Persistent session narrative memory.
 * Stores unified narratives across sessions for continuity references.
 */
export interface SessionNarrativeMemory {
  userId: string;
  sessionId: string;
  timestamp: string;
  dominantState: string;
  unifiedNarrative: string;
  keyThemes: string[];
  riskLevel: 'low' | 'medium' | 'high';
}

/** Per-user session narrative history (last 20 sessions) */
const sessionNarrativeMemory = new Map<string, SessionNarrativeMemory[]>();
const MAX_SESSION_MEMORY = 20;

/**
 * Persists a session's unified narrative for future continuity references.
 */
export function persistSessionNarrativeMemory(
  userId: string,
  memory: Omit<SessionNarrativeMemory, 'userId'>
): void {
  const history = sessionNarrativeMemory.get(userId) ?? [];
  history.push({ userId, ...memory });
  if (history.length > MAX_SESSION_MEMORY) history.shift();
  sessionNarrativeMemory.set(userId, history);
}

/**
 * Retrieves the last N session narrative memories for a user.
 */
export function getSessionNarrativeHistory(
  userId: string,
  limit = 5
): SessionNarrativeMemory[] {
  const history = sessionNarrativeMemory.get(userId) ?? [];
  return history.slice(-limit);
}

/**
 * Builds a continuity reference string for the current session.
 * Example: "This continues the pattern observed in your last session"
 */
export function buildContinuityReference(
  userId: string,
  currentState: string,
  currentRiskLevel: 'low' | 'medium' | 'high'
): string | null {
  const history = sessionNarrativeMemory.get(userId) ?? [];
  if (history.length === 0) return null;

  const lastSession = history[history.length - 1];
  const POSITIVE_STATES = ['Focused', 'Balanced', 'Recovering'];
  const NEGATIVE_STATES = ['Fatigued', 'Stressed', 'Overloaded'];

  const currentPositive = POSITIVE_STATES.includes(currentState);
  const lastPositive = POSITIVE_STATES.includes(lastSession.dominantState);
  const currentNegative = NEGATIVE_STATES.includes(currentState);
  const lastNegative = NEGATIVE_STATES.includes(lastSession.dominantState);

  // Same polarity → continuity
  if ((currentPositive && lastPositive) || (currentNegative && lastNegative)) {
    if (currentState === lastSession.dominantState) {
      return `This continues the pattern observed in your last session.`;
    }
    return `This continues the ${currentPositive ? 'positive' : 'challenging'} pattern observed in your last session.`;
  }

  // Polarity flip → trend reinforcement
  if (currentPositive && lastNegative) {
    return `This marks an improvement from the pattern observed in your last session.`;
  }
  if (currentNegative && lastPositive) {
    return `This represents a shift from the positive pattern observed in your last session.`;
  }

  return null;
}

// ─── Explanation Load Balancing ───────────────────────────────────────────────

/**
 * Adapts explanation length and depth based on user interaction patterns
 * and previous feedback.
 *
 * - User prefers short → prioritise primary layer
 * - Expert user → allow deeper layers
 */
export interface ExplanationLoadBalanceResult {
  /** Which layers to include */
  includeLayers: ('primary' | 'secondary' | 'tertiary')[];
  /** Recommended explanation length: short / standard / full */
  recommendedLength: 'short' | 'standard' | 'full';
  /** Reason for the load balancing decision */
  reason: string;
}

export function balanceExplanationLoad(params: {
  complexityAdjustment: -1 | 0 | 1;
  preferredStyle: 'concise' | 'standard' | 'detailed';
  confidenceLevel: 'low' | 'medium' | 'high';
  riskLevel: 'low' | 'medium' | 'high';
}): ExplanationLoadBalanceResult {
  const { complexityAdjustment, preferredStyle, confidenceLevel, riskLevel } = params;

  // Low confidence → always keep concise to avoid over-explaining uncertainty
  if (confidenceLevel === 'low') {
    return {
      includeLayers: ['primary'],
      recommendedLength: 'short',
      reason: 'Low confidence — concise explanation to avoid over-detailing uncertain insights.',
    };
  }

  // High risk → always include secondary for context, regardless of preference
  if (riskLevel === 'high') {
    return {
      includeLayers: ['primary', 'secondary'],
      recommendedLength: 'standard',
      reason: 'High risk level — secondary layer included for responsible communication.',
    };
  }

  // User prefers short
  if (complexityAdjustment === -1 || preferredStyle === 'concise') {
    return {
      includeLayers: ['primary'],
      recommendedLength: 'short',
      reason: 'User prefers concise explanations based on feedback history.',
    };
  }

  // Expert user
  if (complexityAdjustment === 1 || preferredStyle === 'detailed') {
    return {
      includeLayers: ['primary', 'secondary', 'tertiary'],
      recommendedLength: 'full',
      reason: 'Expert user preference — full explanation depth enabled.',
    };
  }

  // Default: standard
  return {
    includeLayers: ['primary', 'secondary'],
    recommendedLength: 'standard',
    reason: 'Standard explanation depth based on user profile.',
  };
}

// ─── Audit Anomaly Detection ──────────────────────────────────────────────────

/**
 * Tracks audit metrics per user for anomaly detection.
 */
export interface AuditMetrics {
  userId: string;
  selfHealingCount: number;
  contradictionCount: number;
  overrideCount: number;
  totalExplanations: number;
  lastUpdated: string;
}

/** Per-user audit metrics store */
const auditMetricsStore = new Map<string, AuditMetrics>();

/**
 * Updates audit metrics for a user after each explanation generation.
 */
export function updateAuditMetrics(
  userId: string,
  params: {
    selfHealingApplied: boolean;
    contradictionsFound: number;
    overrideApplied: boolean;
  }
): void {
  const existing = auditMetricsStore.get(userId) ?? {
    userId,
    selfHealingCount: 0,
    contradictionCount: 0,
    overrideCount: 0,
    totalExplanations: 0,
    lastUpdated: new Date().toISOString(),
  };

  auditMetricsStore.set(userId, {
    ...existing,
    selfHealingCount: existing.selfHealingCount + (params.selfHealingApplied ? 1 : 0),
    contradictionCount: existing.contradictionCount + params.contradictionFound,
    overrideCount: existing.overrideCount + (params.overrideApplied ? 1 : 0),
    totalExplanations: existing.totalExplanations + 1,
    lastUpdated: new Date().toISOString(),
  });
}

/**
 * Result of an audit anomaly detection check.
 */
export interface AuditAnomalyResult {
  anomalyDetected: boolean;
  flags: string[];
  /** Severity of the anomaly */
  severity: 'none' | 'warning' | 'critical';
  /** Recommended action */
  recommendedAction: string;
  metrics: {
    selfHealingRate: number;
    contradictionRate: number;
    overrideRate: number;
  };
}

/**
 * Detects audit anomalies by analysing:
 *  - Frequent self-healing corrections (>40% of explanations)
 *  - Repeated contradictions (>30% of explanations)
 *  - High override rates (>25% of explanations)
 *
 * Triggers internal alerts and system review flags.
 */
export function detectAuditAnomalies(userId: string): AuditAnomalyResult {
  const metrics = auditMetricsStore.get(userId);
  if (!metrics || metrics.totalExplanations < 5) {
    return {
      anomalyDetected: false,
      flags: [],
      severity: 'none',
      recommendedAction: 'Insufficient data for anomaly detection.',
      metrics: { selfHealingRate: 0, contradictionRate: 0, overrideRate: 0 },
    };
  }

  const selfHealingRate = metrics.selfHealingCount / metrics.totalExplanations;
  const contradictionRate = metrics.contradictionCount / metrics.totalExplanations;
  const overrideRate = metrics.overrideCount / metrics.totalExplanations;

  const flags: string[] = [];
  let severity: 'none' | 'warning' | 'critical' = 'none';

  if (selfHealingRate > 0.4) {
    flags.push(`High self-healing rate (${(selfHealingRate * 100).toFixed(0)}%) — explanation inputs may have systematic inconsistencies.`);
    severity = 'warning';
  }
  if (contradictionRate > 0.3) {
    flags.push(`High contradiction rate (${(contradictionRate * 100).toFixed(0)}%) — state/drift alignment may need review.`);
    severity = 'warning';
  }
  if (overrideRate > 0.25) {
    flags.push(`High override rate (${(overrideRate * 100).toFixed(0)}%) — clinician disagreement may indicate systematic issues.`);
    severity = severity === 'warning' ? 'critical' : 'warning';
  }

  // Critical if multiple flags
  if (flags.length >= 2) severity = 'critical';

  const recommendedAction =
    severity === 'critical' ?'System review recommended — multiple anomaly indicators detected. Flag for clinical validation.'
      : severity === 'warning' ?'Monitor closely — anomaly indicator detected. Consider reviewing explanation templates.' :'No action required.';

  return {
    anomalyDetected: flags.length > 0,
    flags,
    severity,
    recommendedAction,
    metrics: {
      selfHealingRate: Math.round(selfHealingRate * 1000) / 1000,
      contradictionRate: Math.round(contradictionRate * 1000) / 1000,
      overrideRate: Math.round(overrideRate * 1000) / 1000,
    },
  };
}

/**
 * Returns audit metrics for a user.
 */
export function getAuditMetrics(userId: string): AuditMetrics | null {
  return auditMetricsStore.get(userId) ?? null;
}

// ─── Auditability Structure ───────────────────────────────────────────────────

/**
 * Auditability record attached to an explanation for regulatory/clinical validation.
 * Captures the inputs used, high-level transformation steps, and reasoning path.
 */
export interface ExplanationAuditRecord {
  /**
   * Unique version identifier for this explanation.
   * Enables traceability across system updates.
   * Format: exp_<timestamp>_<random>
   */
  version_id: string;
  /** ISO timestamp of when the explanation was generated */
  generatedAt: string;
  /** Inputs used to produce the explanation */
  inputs: {
    stateLabel?: string;
    driftType?: string;
    driftCategory?: string;
    driftClassification?: string;
    stabilityScore?: number;
    resilienceScore?: number;
    confidence?: number;
    signalAgreement?: number;
    dataFreshnessMs?: number;
    audience?: string;
    severity?: string;
    confidenceLevel?: string;
  };
  /** High-level transformation steps applied */
  transformationSteps: string[];
  /** Reasoning path — ordered list of decisions made */
  reasoningPath: string[];
  /** Whether any self-healing corrections were applied */
  selfHealingApplied: boolean;
  /** Violations detected and how they were resolved */
  resolvedViolations: string[];
  /** Pre-generation adjustments applied */
  preGenerationAdjustments?: string[];
  /** Risk level classification */
  riskLevel?: ExplanationRiskLevel;
  /** Stability check result */
  stabilityCheck?: {
    instabilitySeverity: 'none' | 'minor' | 'major';
    continuityReferenceApplied: boolean;
  };
  /** Wording changes from previous version */
  wordingChanges?: string[];
  /** Reasoning changes from previous version */
  reasoningChanges?: string[];
}

/**
 * Builds an auditability record for an explanation.
 * Intended to be stored in researchLoggingService for future regulatory review.
 */
export function buildAuditRecord(params: {
  inputs: ExplanationAuditRecord['inputs'];
  transformationSteps: string[];
  reasoningPath: string[];
  selfHealingApplied?: boolean;
  resolvedViolations?: string[];
  preGenerationAdjustments?: string[];
  riskLevel?: ExplanationRiskLevel;
  stabilityCheck?: ExplanationAuditRecord['stabilityCheck'];
  previousVersionId?: string;
  wordingChanges?: string[];
  reasoningChanges?: string[];
}): ExplanationAuditRecord {
  return {
    version_id: generateExplanationVersionId(),
    generatedAt: new Date().toISOString(),
    inputs: params.inputs,
    transformationSteps: params.transformationSteps,
    reasoningPath: params.reasoningPath,
    selfHealingApplied: params.selfHealingApplied ?? false,
    resolvedViolations: params.resolvedViolations ?? [],
    preGenerationAdjustments: params.preGenerationAdjustments,
    riskLevel: params.riskLevel,
    stabilityCheck: params.stabilityCheck,
    wordingChanges: params.wordingChanges,
    reasoningChanges: params.reasoningChanges,
  };
}

// ─── Consistency Validation (Self-Healing) ────────────────────────────────────

export interface ConsistencyValidationResult {
  valid: boolean;
  violations: string[];
  /** Healed explanation — adjusted to resolve contradictions */
  healedExplanation?: string;
  /** Audit trail of corrections applied */
  corrections: string[];
}

/**
 * Validates that the explanation components are internally consistent.
 * When a contradiction is detected, it ADJUSTS the explanation instead of
 * just flagging it (self-healing behaviour).
 *
 * Self-healing examples:
 *   - "Fatigued" but positive drift → soften fatigue language, add recovery note
 *   - "Focused" but negative drift → add caution note
 *   - Chronic drift but positive drift type → reclassify language
 *
 * Checks:
 *   - drift classification aligns with state explanation
 *   - stability/resilience interpretations match their scores
 *   - prediction explanation matches underlying signals
 */
export function validateExplanationConsistency(params: {
  stateLabel: HumanStateLabel;
  driftType?: DriftType;
  driftCategory?: DriftCategory;
  driftClassification?: DriftClassification;
  stabilityScore?: number;
  resilienceScore?: number;
  confidence?: number;
  predictedPerformance?: number;
  horizonHours?: number;
  /** Current explanation text to heal if contradictions found */
  explanationText?: string;
}): ConsistencyValidationResult {
  const violations: string[] = [];
  const corrections: string[] = [];
  let healedExplanation = params.explanationText ?? '';

  const {
    stateLabel,
    driftType,
    driftCategory,
    driftClassification,
    stabilityScore,
    resilienceScore,
    confidence,
    predictedPerformance,
    horizonHours,
  } = params;

  // 1. Positive drift but Fatigued/Overloaded state
  //    → Self-heal: soften fatigue language, add recovery note
  if (driftType === 'positive' && (stateLabel === 'Fatigued' || stateLabel === 'Overloaded')) {
    violations.push(
      `Contradiction: positive drift but state is "${stateLabel}" — state should reflect improvement.`
    );
    if (healedExplanation) {
      // Soften fatigue/overload language
      healedExplanation = healedExplanation
        .replace(/reduced cognitive capacity/gi, 'some cognitive strain')
        .replace(/Rest or a short break is recommended/gi, 'early signs of recovery are present')
        .replace(/Simplify tasks and reduce demands immediately/gi, 'signals are beginning to stabilise');
      // Add recovery note
      healedExplanation += ' Recent signals suggest early recovery may be underway.';
      corrections.push(
        `Softened "${stateLabel}" language and added recovery note due to positive drift.`
      );
    }
  }

  // 2. Negative drift but Focused state
  //    → Self-heal: add caution note
  if (driftType === 'negative' && stateLabel === 'Focused') {
    violations.push(
      `Contradiction: negative drift but state is "Focused" — state should reflect decline.`
    );
    if (healedExplanation) {
      healedExplanation += ' Note: recent signals show some variability — monitor for changes.';
      corrections.push('Added variability caution note due to negative drift in Focused state.');
    }
  }

  // 3. Fatigue-related drift but Focused state
  //    → Self-heal: add early fatigue warning
  if (driftCategory === 'fatigue-related' && stateLabel === 'Focused') {
    violations.push(
      `Contradiction: fatigue-related drift but state is "Focused".`
    );
    if (healedExplanation) {
      healedExplanation += ' Early fatigue signals are present — consider a short break soon.';
      corrections.push('Added early fatigue warning due to fatigue-related drift in Focused state.');
    }
  }

  // 4. Performance-improvement drift but Fatigued state
  //    → Self-heal: soften fatigue, note improvement
  if (driftCategory === 'performance-improvement' && stateLabel === 'Fatigued') {
    violations.push(
      `Contradiction: performance-improvement drift but state is "Fatigued".`
    );
    if (healedExplanation) {
      healedExplanation = healedExplanation
        .replace(/Rest or a short break is recommended/gi, 'performance signals are improving');
      healedExplanation += ' Performance metrics are trending positively.';
      corrections.push('Softened fatigue language and added improvement note due to performance-improvement drift.');
    }
  }

  // 5. Chronic drift but positive drift type
  //    → Self-heal: clarify classification language
  if (driftClassification === 'chronic' && driftType === 'positive') {
    violations.push(
      `Contradiction: chronic drift classification but drift type is positive — chronic implies sustained negative pattern.`
    );
    if (healedExplanation) {
      healedExplanation = healedExplanation
        .replace(/Ongoing decline pattern detected/gi, 'Repeated state variability detected');
      corrections.push('Replaced "Ongoing decline" with "Repeated state variability" due to positive drift type.');
    }
  }

  // 6. High stability but Overloaded state
  //    → Self-heal: soften overloaded language
  if (stabilityScore != null && stabilityScore >= 80 && stateLabel === 'Overloaded') {
    violations.push(
      `Contradiction: high stability score (${stabilityScore}) but state is "Overloaded".`
    );
    if (healedExplanation) {
      healedExplanation = healedExplanation
        .replace(/Cognitive load is critically high/gi, 'Cognitive load is elevated')
        .replace(/Simplify tasks and reduce demands immediately/gi, 'Consider pacing your tasks');
      corrections.push('Softened "Overloaded" language due to high stability score.');
    }
  }

  // 7. High resilience but chronic drift
  //    → Self-heal: add recovery capacity note
  if (
    resilienceScore != null &&
    resilienceScore >= 75 &&
    driftClassification === 'chronic'
  ) {
    violations.push(
      `Contradiction: high resilience score (${resilienceScore}) but drift is classified as chronic — high resilience implies fast recovery.`
    );
    if (healedExplanation) {
      healedExplanation += ` Your strong recovery capacity (resilience: ${resilienceScore}/100) suggests this pattern may resolve quickly.`;
      corrections.push('Added recovery capacity note due to high resilience with chronic drift.');
    }
  }

  // 8. Low confidence but very high predicted performance
  if (
    confidence != null &&
    confidence < 0.4 &&
    predictedPerformance != null &&
    predictedPerformance > 85
  ) {
    violations.push(
      `Contradiction: low confidence (${(confidence * 100).toFixed(0)}%) but predicted performance is very high (${predictedPerformance}) — high predictions require sufficient signal confidence.`
    );
    if (healedExplanation) {
      healedExplanation = healedExplanation
        .replace(
          new RegExp(`${predictedPerformance}/100`, 'g'),
          `approximately ${Math.round(predictedPerformance * 0.85)}/100`
        );
      corrections.push('Adjusted predicted performance downward due to low confidence.');
    }
  }

  // 9. Invalid prediction horizon
  if (horizonHours != null && horizonHours <= 0) {
    violations.push(`Invalid prediction horizon: ${horizonHours}h — must be positive.`);
  }

  return {
    valid: violations.length === 0,
    violations,
    healedExplanation: corrections.length > 0 ? healedExplanation : undefined,
    corrections,
  };
}

// ─── Longitudinal Summaries ───────────────────────────────────────────────────

export interface LongitudinalTrend {
  /** Array of scores ordered oldest → newest */
  scores: number[];
  /** Label for the metric being trended */
  metricLabel: string;
}

/**
 * Derives a simple trend direction from a score series.
 */
function deriveTrend(scores: number[]): 'improving' | 'declining' | 'stable' {
  if (scores.length < 2) return 'stable';
  const first = scores.slice(0, Math.ceil(scores.length / 2));
  const last = scores.slice(Math.floor(scores.length / 2));
  const avgFirst = first.reduce((a, b) => a + b, 0) / first.length;
  const avgLast = last.reduce((a, b) => a + b, 0) / last.length;
  const delta = avgLast - avgFirst;
  if (delta > 5) return 'improving';
  if (delta < -5) return 'declining';
  return 'stable';
}

export interface LongitudinalSummaryResult {
  summary: string;
  stabilityNarrative: string;
  resilienceNarrative: string;
  driftNarrative: string;
  translationKey: string;
  /**
   * Comparative narrative: current period vs previous period.
   * Example: "Your stability has improved compared to last week,
   *           though short-term variability remains."
   */
  comparativeNarrative?: string;
  /** Short-term vs long-term trend comparison */
  shortVsLongTermNarrative?: string;
}

/**
 * Generates a longitudinal narrative from historical trends.
 * Now includes:
 *   - current vs last period comparison
 *   - short-term vs long-term trend comparison
 *
 * Examples:
 *   "Your stability has improved over the past week" *"Your stability has improved compared to last week,
 *    though short-term variability remains."
 */
export function buildLongitudinalSummary(params: {
  stabilityScores: number[];
  resilienceScores: number[];
  driftEventCounts: number[]; // per-day drift event counts
  periodLabel?: string; // e.g. "past week", "past month"
  audience?: ExplanationAudience;
  /**
   * Previous period scores for comparison (same length as current period).
   * When provided, enables current vs last period narrative.
   */
  previousPeriod?: {
    stabilityScores: number[];
    resilienceScores: number[];
    driftEventCounts: number[];
    periodLabel?: string;
  };
}): LongitudinalSummaryResult {
  const {
    stabilityScores,
    resilienceScores,
    driftEventCounts,
    periodLabel = 'past week',
    audience = 'patient',
    previousPeriod,
  } = params;

  const stabilityTrend = deriveTrend(stabilityScores);
  const resilienceTrend = deriveTrend(resilienceScores);
  const driftTrend = deriveTrend(driftEventCounts.map((c) => -c)); // invert: fewer events = improving

  // Stability narrative
  const stabilityNarrative =
    stabilityTrend === 'improving'
      ? audience === 'clinician'
        ? `Stability index shows a positive trend over the ${periodLabel}.`
        : `Your stability has improved over the ${periodLabel}.`
      : stabilityTrend === 'declining'
      ? audience === 'clinician'
        ? `Stability index shows a declining trend over the ${periodLabel}.`
        : `Your stability has decreased over the ${periodLabel}.`
      : audience === 'clinician'
      ? `Stability index is consistent over the ${periodLabel}.`
      : `Your stability has been consistent over the ${periodLabel}.`;

  // Resilience narrative
  const resilienceNarrative =
    resilienceTrend === 'improving'
      ? audience === 'clinician'
        ? `Resilience score is trending upward — recovery capacity improving.`
        : `You've shown consistent recovery patterns recently.`
      : resilienceTrend === 'declining'
      ? audience === 'clinician'
        ? `Resilience score is declining — recovery capacity may be reduced.`
        : `Your recovery patterns have slowed recently.`
      : audience === 'clinician'
      ? `Resilience score is stable.`
      : `Your recovery patterns are steady.`;

  // Drift narrative
  const driftNarrative =
    driftTrend === 'improving'
      ? audience === 'clinician'
        ? `Drift frequency is decreasing — cognitive state is stabilising.`
        : `Your cognitive state has been more stable recently.`
      : driftTrend === 'declining'
      ? audience === 'clinician'
        ? `Drift frequency is increasing — monitor for structural cognitive change.`
        : `There have been more state changes recently — consider rest or recovery activities.`
      : audience === 'clinician'
      ? `Drift frequency is within expected range.`
      : `Your cognitive state has been consistent.`;

  // Overall summary
  const positiveCount = [stabilityTrend, resilienceTrend, driftTrend].filter(
    (t) => t === 'improving'
  ).length;
  const negativeCount = [stabilityTrend, resilienceTrend, driftTrend].filter(
    (t) => t === 'declining'
  ).length;

  let summary: string;
  let translationKey: string;

  if (positiveCount >= 2) {
    summary =
      audience === 'clinician'
        ? `Overall cognitive trajectory is positive over the ${periodLabel}.`
        : `Overall, your cognitive health has been improving over the ${periodLabel}.`;
    translationKey = TRANSLATION_KEYS.longitudinal.stabilityImproved;
  } else if (negativeCount >= 2) {
    summary =
      audience === 'clinician'
        ? `Overall cognitive trajectory shows decline over the ${periodLabel}. Review recommended.`
        : `Your cognitive patterns suggest some strain over the ${periodLabel}. Rest and recovery may help.`;
    translationKey = TRANSLATION_KEYS.longitudinal.stabilityDeclined;
  } else {
    summary =
      audience === 'clinician'
        ? `Cognitive metrics are within expected range over the ${periodLabel}.`
        : `Your cognitive health has been steady over the ${periodLabel}.`;
    translationKey = TRANSLATION_KEYS.longitudinal.stabilityStable;
  }

  // ── Current vs Previous Period Comparison ─────────────────────────────────
  let comparativeNarrative: string | undefined;
  if (previousPeriod) {
    const prevStabilityTrend = deriveTrend(previousPeriod.stabilityScores);
    const prevResilienceTrend = deriveTrend(previousPeriod.resilienceScores);
    const prevPeriodLabel = previousPeriod.periodLabel ?? 'last week';

    const currentAvgStability =
      stabilityScores.length > 0
        ? stabilityScores.reduce((a, b) => a + b, 0) / stabilityScores.length
        : 0;
    const prevAvgStability =
      previousPeriod.stabilityScores.length > 0
        ? previousPeriod.stabilityScores.reduce((a, b) => a + b, 0) /
          previousPeriod.stabilityScores.length
        : 0;

    const stabilityDelta = currentAvgStability - prevAvgStability;
    const stabilityChanged =
      stabilityDelta > 5
        ? 'improved'
        : stabilityDelta < -5
        ? 'declined' :'remained stable';

    const currentAvgDrift =
      driftEventCounts.length > 0
        ? driftEventCounts.reduce((a, b) => a + b, 0) / driftEventCounts.length
        : 0;
    const prevAvgDrift =
      previousPeriod.driftEventCounts.length > 0
        ? previousPeriod.driftEventCounts.reduce((a, b) => a + b, 0) /
          previousPeriod.driftEventCounts.length
        : 0;

    const driftDelta = currentAvgDrift - prevAvgDrift;
    const shortTermVariability =
      driftDelta > 0.5 ? 'though short-term variability remains' : '';

    if (audience === 'clinician') {
      comparativeNarrative = `Stability ${stabilityChanged} compared to ${prevPeriodLabel} (Δ${stabilityDelta > 0 ? '+' : ''}${stabilityDelta.toFixed(1)}). Resilience trend: ${resilienceTrend} vs ${prevResilienceTrend} in ${prevPeriodLabel}.${shortTermVariability ? ` Note: ${shortTermVariability}.` : ''}`;
    } else {
      if (stabilityChanged === 'improved') {
        comparativeNarrative = `Your stability has improved compared to ${prevPeriodLabel}${shortTermVariability ? `, ${shortTermVariability}` : ''}.`;
      } else if (stabilityChanged === 'declined') {
        comparativeNarrative = `Your stability has declined compared to ${prevPeriodLabel}${shortTermVariability ? `, and ${shortTermVariability}` : ''}.`;
      } else {
        comparativeNarrative = `Your stability is similar to ${prevPeriodLabel}${shortTermVariability ? `, though ${shortTermVariability}` : ''}.`;
      }
    }
  }

  // ── Short-term vs Long-term Trend Comparison ──────────────────────────────
  let shortVsLongTermNarrative: string | undefined;
  if (stabilityScores.length >= 4) {
    const shortTermScores = stabilityScores.slice(-Math.ceil(stabilityScores.length / 3));
    const longTermScores = stabilityScores;
    const shortTrend = deriveTrend(shortTermScores);
    const longTrend = deriveTrend(longTermScores);

    if (shortTrend !== longTrend) {
      if (audience === 'clinician') {
        shortVsLongTermNarrative = `Short-term trend: ${shortTrend}. Long-term trend: ${longTrend}. Divergence detected — monitor for sustained change.`;
      } else {
        if (shortTrend === 'improving' && longTrend === 'declining') {
          shortVsLongTermNarrative =
            'Your recent sessions show improvement, though the longer-term trend has been declining.';
        } else if (shortTrend === 'declining' && longTrend === 'improving') {
          shortVsLongTermNarrative =
            'Your overall trend has been positive, though recent sessions show some variability.';
        } else {
          shortVsLongTermNarrative = `Recent sessions are ${shortTrend}, while the longer-term pattern is ${longTrend}.`;
        }
      }
    } else {
      shortVsLongTermNarrative =
        audience === 'clinician'
          ? `Both short-term and long-term trends are ${longTrend}.`
          : `Both your recent and overall patterns are ${longTrend}.`;
    }
  }

  return {
    summary,
    stabilityNarrative,
    resilienceNarrative,
    driftNarrative,
    translationKey,
    comparativeNarrative,
    shortVsLongTermNarrative,
  };
}

// ─── Contextual Adaptive Explanation Builder ──────────────────────────────────

/**
 * Builds a fully adaptive, traceable, multi-format explanation.
 *
 * Adapts tone and depth based on:
 *   - audience: patient → simple, reassuring; clinician → precise, detailed
 *   - confidenceLevel: low → cautious language; high → allow deeper explanations
 *   - severity: significant → clearer guidance
 *
 * Confidence-gated depth:
 *   - High confidence  → allow deeper explanations (secondary + tertiary layers)
 *   - Medium           → show primary + limited secondary
 *   - Low              → keep concise, avoid over-detail (primary only)
 *
 * Returns a FormattedExplanation with short, detailed, notification, report
 * versions plus contributing factors, translation key, confidence breakdown,
 * and three-layer priority structure.
 */
export function buildContextualExplanation(params: {
  baseExplanation: string;
  context: ExplanationContext;
  factors?: ContributingFactor[];
  stateLabel?: HumanStateLabel;
  translationKey?: string;
  /** Optional confidence breakdown inputs */
  confidenceBreakdownInputs?: {
    signalAgreement: number;
    dataFreshnessMs: number;
    stabilityScore: number;
  };
  /** Optional temporal context label */
  temporalContext?: string;
}): FormattedExplanation {
  const {
    baseExplanation,
    context,
    factors = [],
    stateLabel,
    translationKey = 'explanation.generic',
    confidenceBreakdownInputs,
    temporalContext,
  } = params;

  const { audience, confidenceLevel, severity } = context;
  const severityWord = getSeverityWording(severity);
  const factorDescriptions = buildFactorDescriptions(factors);
  const factorPreamble = buildFactorPreamble(factors);

  // ── Uncertainty language tier ──────────────────────────────────────────────
  const uncertaintyPrefix = getUncertaintyPrefix(confidenceLevel);

  // ── Confidence prefix ──────────────────────────────────────────────────────
  const confidencePrefix =
    confidenceLevel === 'low'
      ? audience === 'clinician' ?'Signal confidence is low — interpret with caution. ' :'We have limited data right now, so this is an estimate. '
      : confidenceLevel === 'medium'
      ? audience === 'clinician' ?'Moderate signal confidence. ' :'' :'';

  // ── Severity guidance suffix ───────────────────────────────────────────────
  const severityGuidance =
    severity === 'significant'
      ? audience === 'clinician'
        ? ` A ${severityWord} was detected — clinical review may be warranted.`
        : ` A ${severityWord} was detected — consider taking a break or speaking with your care team.`
      : severity === 'moderate'
      ? audience === 'clinician'
        ? ` A ${severityWord} is present.`
        : ` A ${severityWord} has been noted.`
      : '';

  // ── Factor sentence ────────────────────────────────────────────────────────
  const factorSentence =
    factorPreamble.length > 0
      ? audience === 'clinician'
        ? ` ${factorPreamble}.`
        : ` ${factorPreamble}.`
      : '';

  // ── Temporal suffix ────────────────────────────────────────────────────────
  const temporalSuffix = temporalContext ? ` (${temporalContext})` : '';

  // ── Short version (1 line) — uses uncertainty language tier ───────────────
  const short =
    audience === 'clinician'
      ? `${stateLabel ?? 'State'}: ${severityWord} detected.${confidenceLevel === 'low' ? ' (Low confidence)' : ''}`
      : stateLabel
      ? `${uncertaintyPrefix} ${stateLabel.toLowerCase()} state.`
      : `${confidencePrefix}${baseExplanation}`;

  // ── Confidence-gated detailed version ─────────────────────────────────────
  // High confidence → full detail; Medium → moderate; Low → concise only
  const detailed =
    confidenceLevel === 'low'
      ? `${confidencePrefix}${baseExplanation}` // concise, no factor detail
      : `${confidencePrefix}${baseExplanation}${factorSentence}${severityGuidance}${temporalSuffix}`;

  // ── Notification (≤80 chars) ───────────────────────────────────────────────
  const notificationBase =
    audience === 'clinician'
      ? `${stateLabel ?? 'State'}: ${severityWord}.`
      : severity === 'significant'
      ? `A ${severityWord} was detected in your cognitive state.`
      : `Your cognitive state shows a ${severityWord}.`;
  const notification = notificationBase.slice(0, 80);

  // ── Report version (formal) ────────────────────────────────────────────────
  const report =
    audience === 'clinician'
      ? `${confidencePrefix}Assessment: ${baseExplanation}${factorSentence} Severity classification: ${severity}. ${severityGuidance.trim()}`
      : `${confidencePrefix}${baseExplanation}${factorSentence}${severityGuidance}`;

  // ── Confidence breakdown ───────────────────────────────────────────────────
  const confidence_explanation = confidenceBreakdownInputs
    ? buildConfidenceBreakdown(confidenceBreakdownInputs)
    : undefined;

  const formatted: FormattedExplanation = {
    short,
    detailed,
    notification,
    report,
    factors: factorDescriptions,
    translationKey,
    confidence_explanation,
  };

  // ── Confidence-gated three-layer priority structure ────────────────────────
  // High confidence → full secondary + tertiary
  // Medium          → primary + limited secondary (first factor only)
  // Low             → primary only (no secondary/tertiary to avoid over-detail)
  if (confidenceLevel === 'high') {
    formatted.layers = buildLayeredExplanation(formatted, factorDescriptions);
  } else if (confidenceLevel === 'medium') {
    formatted.layers = {
      primary: formatted.short,
      secondary: factorDescriptions.length > 0
        ? `Key factor: ${factorDescriptions[0]}.`
        : formatted.notification,
      tertiary: formatted.detailed,
    };
  } else {
    // Low confidence: primary only
    formatted.layers = {
      primary: formatted.short,
      secondary: '',
      tertiary: '',
    };
  }

  return formatted;
}

// ─── Shared Terminology ───────────────────────────────────────────────────────

/** Canonical metric display names used across all explanation strings */
export const METRIC_LABELS: Record<string, string> = {
  hrv: 'HRV',
  rmssd: 'HRV',
  reactionTimeMs: 'reaction time',
  attentionScore: 'attention',
  focusScore: 'focus',
  mentalEnergy: 'mental energy',
  compositeScore: 'cognitive score',
  sleepQuality: 'sleep quality',
  sleepDuration: 'sleep duration',
  cognitiveLoad: 'cognitive load',
  heartRate: 'heart rate',
  restingHeartRate: 'resting heart rate',
  activitySteps: 'activity level',
  workingMemoryAccuracy: 'working memory',
  cognitiveFlexibility: 'cognitive flexibility',
  thetaAlphaRatio: 'neural activity ratio',
  recoveryScore: 'recovery score',
};

export function getMetricLabel(metric: string): string {
  return METRIC_LABELS[metric] ?? metric;
}

// ─── Human State Explanations ─────────────────────────────────────────────────

export type HumanStateLabel =
  | 'Focused' | 'Fatigued' | 'Stressed' | 'Recovering' | 'Overloaded' | 'Balanced';

/**
 * Returns the canonical explanation for a given human state label.
 * Used by both the service layer and API routes to ensure identical wording.
 */
export const STATE_EXPLANATIONS: Record<HumanStateLabel, string> = {
  Focused:
    'Your attention, reaction speed, and neural signals indicate a high-performance cognitive state.',
  Fatigued:
    'Multiple signals suggest reduced cognitive capacity. Rest or a short break is recommended.',
  Stressed:
    'Elevated physiological stress markers detected. HRV and heart rate indicate autonomic tension.',
  Recovering:
    'Your system is in active recovery. Cognitive performance may be below your peak.',
  Overloaded:
    'Cognitive load is critically high. Simplify tasks and reduce demands immediately.',
  Balanced:
    'Your cognitive and physiological signals are within normal range.',
};

export function getStateExplanation(label: HumanStateLabel): string {
  return STATE_EXPLANATIONS[label];
}

/**
 * Builds a full state explanation that optionally incorporates drift context,
 * pending transition information, and contextual audience/severity adaptation.
 */
export function buildStateExplanation(
  label: HumanStateLabel,
  options?: {
    driftAdjusted?: boolean;
    driftReason?: string;
    transitionPending?: boolean;
    candidateLabel?: HumanStateLabel;
    candidateDurationMs?: number;
    candidateRequiredMs?: number;
    context?: ExplanationContext;
    factors?: ContributingFactor[];
    confidenceBreakdownInputs?: {
      signalAgreement: number;
      dataFreshnessMs: number;
      stabilityScore: number;
    };
    temporalContext?: string;
  }
): string {
  const base = STATE_EXPLANATIONS[label];

  // If context provided, delegate to contextual builder and return detailed version
  if (options?.context) {
    const formatted = buildContextualExplanation({
      baseExplanation: base,
      context: options.context,
      factors: options?.factors ?? [],
      stateLabel: label,
      translationKey: TRANSLATION_KEYS.state[label],
      confidenceBreakdownInputs: options?.confidenceBreakdownInputs,
      temporalContext: options?.temporalContext,
    });
    let result = formatted.detailed;

    if (options?.driftAdjusted && options.driftReason) {
      result += ` (State adjusted based on recent signal change: ${options.driftReason})`;
    }
    if (options?.transitionPending && options.candidateLabel) {
      const remainingSec = Math.round(
        ((options.candidateRequiredMs ?? 0) - (options.candidateDurationMs ?? 0)) / 1000
      );
      result += ` (Transition to "${options.candidateLabel}" pending — ${remainingSec}s of sustained signal remaining)`;
    }
    return result;
  }

  let explanation = base;

  if (options?.driftAdjusted && options.driftReason) {
    explanation += ` (State adjusted based on recent signal change: ${options.driftReason})`;
  }

  if (options?.transitionPending && options.candidateLabel) {
    const remainingSec = Math.round(
      ((options.candidateRequiredMs ?? 0) - (options.candidateDurationMs ?? 0)) / 1000
    );
    explanation += ` (Transition to "${options.candidateLabel}" pending — ${remainingSec}s of sustained signal remaining)`;
  }

  return explanation;
}

// ─── Drift Reason Explanations ────────────────────────────────────────────────

export type DriftCategory =
  | 'fatigue-related' | 'stress-related' | 'recovery-related' | 'performance-improvement' | 'none';

export type DriftType = 'positive' | 'negative' | 'neutral';

export interface DriftSignalSummary {
  metric: string;
  cachedValue: number;
  incomingValue: number;
  changePct: number;
}

/**
 * Generates a human-readable drift reason string from the primary exceeded signal
 * and the semantic drift category. Used consistently across all API routes.
 */
export function buildDriftReason(
  driftCategory: DriftCategory,
  primarySignal: DriftSignalSummary
): string {
  const primaryLabel = getMetricLabel(primarySignal.metric);
  const direction =
    primarySignal.incomingValue > primarySignal.cachedValue ? 'increase' : 'drop';

  const templates: Record<DriftCategory, string> = {
    'fatigue-related': `HRV drop + ${
      primaryLabel === 'reaction time' ? 'slower reaction' : 'lower ' + primaryLabel
    } → likely fatigue`,
    'stress-related': `HRV drop + reduced cognitive score → likely stress`,
    'recovery-related': `HRV rise + improved ${primaryLabel} → recovery in progress`,
    'performance-improvement': `Faster reaction + higher ${primaryLabel} → performance improvement`,
    none: `${primaryLabel} ${direction} of ${primarySignal.changePct.toFixed(0)}% detected`,
  };

  return templates[driftCategory];
}

/**
 * Returns a user-facing message for a drift event.
 * Kept non-alarming and informative.
 * Optionally adapts to audience context.
 */
export function buildDriftUserMessage(
  driftCategory: DriftCategory,
  driftType: DriftType,
  context?: ExplanationContext
): string {
  const audience = context?.audience ?? 'patient';
  let severity = context?.severity ?? 'moderate';
  const severityWord = getSeverityWording(severity);

  if (driftType === 'positive') {
    return audience === 'clinician' ?'Positive drift detected — cognitive state has improved.' :'Your state has improved since the last session.';
  }
  if (driftCategory === 'fatigue-related') {
    return audience === 'clinician'
      ? `Fatigue-related drift detected — ${severityWord} in HRV and reaction metrics.`
      : 'Your state changed significantly — signs of fatigue detected.';
  }
  if (driftCategory === 'stress-related') {
    return audience === 'clinician'
      ? `Stress-related drift detected — ${severityWord} in autonomic markers.`
      : 'Your state changed significantly — elevated stress signals detected.';
  }
  if (driftCategory === 'recovery-related') {
    return audience === 'clinician'
      ? `Recovery drift detected — ${severityWord} in recovery indicators.`
      : 'Your state changed significantly — recovery in progress.';
  }
  return audience === 'clinician'
    ? `Drift detected — ${severityWord} in cognitive signals.`
    : 'Your state changed significantly in the last session.';
}

// ─── Drift Classification Messages ───────────────────────────────────────────

export type DriftClassification = 'acute' | 'chronic' | 'none';

/**
 * Returns the canonical label and message for a drift classification.
 * Ensures "Short-term fatigue detected" and "Ongoing decline pattern detected"
 * are worded identically everywhere they appear.
 */
export function getDriftClassificationText(
  classification: DriftClassification,
  persistenceRatio: number = 0,
  context?: ExplanationContext
): { label: string; message: string; translationKey: string } {
  const audience = context?.audience ?? 'patient';

  switch (classification) {
    case 'chronic':
      return {
        label: 'Ongoing decline pattern detected',
        message:
          audience === 'clinician' ?'Chronic drift pattern identified — repeated negative drift events suggest a structural change in cognitive state requiring clinical attention.' :'Ongoing decline pattern detected — repeated negative drift suggests a structural change in cognitive state.',
        translationKey: TRANSLATION_KEYS.drift.classification.chronic,
      };
    case 'acute':
      return {
        label: 'Short-term fatigue detected',
        message:
          audience === 'clinician'
            ? `Acute drift pattern — transient state fluctuation (persistence ratio: ${(persistenceRatio * 100).toFixed(0)}%).`
            : persistenceRatio < 0.4
            ? 'Short-term fatigue detected — variable signals suggest transient state changes.'
            : 'Short-term fatigue detected — this appears to be a momentary fluctuation rather than a lasting change.',
        translationKey: TRANSLATION_KEYS.drift.classification.acute,
      };
    case 'none':
    default:
      return {
        label: 'Stable',
        message: 'No significant drift detected today.',
        translationKey: TRANSLATION_KEYS.drift.classification.none,
      };
  }
}

// ─── Stability & Resilience Interpretations ───────────────────────────────────

/**
 * Returns the canonical stability interpretation string.
 * Used in biological age, predictions, and Your Brain Today.
 */
export function getStabilityInterpretation(
  stabilityScore: number,
  context?: ExplanationContext
): string {
  const audience = context?.audience ?? 'patient';
  if (stabilityScore >= 80) {
    return audience === 'clinician'
      ? `High stability index (${stabilityScore}/100) — consistent inter-session cognitive performance.`
      : 'High stability — consistent performance across sessions';
  }
  if (stabilityScore >= 50) {
    return audience === 'clinician'
      ? `Moderate stability index (${stabilityScore}/100) — some inter-session variability present.`
      : 'Moderate stability — some variability in cognitive performance';
  }
  return audience === 'clinician'
    ? `Low stability index (${stabilityScore}/100) — significant inter-session variability detected.`
    : 'Low stability — significant variability detected';
}

/**
 * Returns the canonical resilience interpretation string.
 * Used in biological age, predictions, and Your Brain Today.
 */
export function getResilienceInterpretation(
  resilienceScore: number,
  context?: ExplanationContext
): string {
  const audience = context?.audience ?? 'patient';
  if (resilienceScore >= 75) {
    return audience === 'clinician'
      ? `High resilience score (${resilienceScore}/100) — rapid post-drift recovery observed.`
      : 'High resilience — fast recovery after drift events';
  }
  if (resilienceScore >= 50) {
    return audience === 'clinician'
      ? `Moderate resilience score (${resilienceScore}/100) — recovery from state changes is ongoing.`
      : 'Moderate resilience — recovering from state changes';
  }
  return audience === 'clinician'
    ? `Low resilience score (${resilienceScore}/100) — slow post-drift recovery pattern detected.`
    : 'Low resilience — slow recovery pattern detected';
}

// ─── Confidence Explanations ──────────────────────────────────────────────────

export type ConfidenceLabel = 'High' | 'Moderate' | 'Low';

/**
 * Returns the canonical confidence label from a 0–1 confidence score.
 */
export function getConfidenceLabel(confidence: number): ConfidenceLabel {
  if (confidence >= 0.75) return 'High';
  if (confidence >= 0.5) return 'Moderate';
  return 'Low';
}

/**
 * Returns a user-facing explanation for the current confidence level,
 * incorporating decay factors, boost factors, and optional audience context.
 */
export function buildConfidenceExplanation(
  confidence: number,
  decayFactors: string[],
  boostFactors: string[],
  context?: ExplanationContext
): string {
  const label = getConfidenceLabel(confidence);
  let score = Math.round(confidence * 100);
  const audience = context?.audience ?? 'patient';

  if (label === 'High') {
    const boostNote =
      boostFactors.length > 0 ? ` Boosted by: ${boostFactors.join(', ')}.` : '';
    return audience === 'clinician'
      ? `High signal confidence (${score}%) — multi-signal agreement and fresh data.${boostNote}`
      : `High confidence (${score}%) — signals are consistent and data is fresh.${boostNote}`;
  }

  if (label === 'Moderate') {
    const decayNote =
      decayFactors.length > 0 ? ` Reduced by: ${decayFactors.join(', ')}.` : '';
    return audience === 'clinician'
      ? `Moderate signal confidence (${score}%) — some uncertainty present.${decayNote}`
      : `Moderate confidence (${score}%) — some uncertainty in the current reading.${decayNote}`;
  }

  const decayNote =
    decayFactors.length > 0 ? ` Factors: ${decayFactors.join(', ')}.` : '';
  return audience === 'clinician'
    ? `Low signal confidence (${score}%) — data may be stale or signals conflicting.${decayNote}`
    : `Low confidence (${score}%) — signals may be inconsistent or data is stale.${decayNote}`;
}

// ─── Biological Age Explanations ─────────────────────────────────────────────

/**
 * Returns a user-facing explanation for a biological age delta.
 * Consistent framing used in both the service and API route.
 */
export function buildBioAgeDeltaExplanation(
  delta: number | null,
  domain: 'brain' | 'heart'
): string {
  if (delta == null) {
    return `Your ${domain} age estimate is based on available biomarker data.`;
  }
  const domainLabel = domain === 'brain' ? 'brain' : 'heart';
  if (delta <= -3) {
    return `Your ${domainLabel} is performing significantly younger than your chronological age — excellent result.`;
  }
  if (delta < 0) {
    return `Your ${domainLabel} is performing younger than your chronological age — keep it up.`;
  }
  if (delta === 0) {
    return `Your ${domainLabel} age aligns with your chronological age — within healthy range.`;
  }
  if (delta <= 3) {
    return `Your ${domainLabel} age is slightly above your chronological age — targeted training can help.`;
  }
  return `Your ${domainLabel} age is above your chronological age — consistent training and lifestyle changes can improve this.`;
}

/**
 * Returns the canonical drift age offset explanation for biological age.
 */
export function buildDriftAgeOffsetExplanation(
  offset: number,
  driftEventsToday: number,
  resilienceScore: number
): string {
  if (offset > 0) {
    return `Repeated negative drift pattern detected — ${driftEventsToday} state change(s) today suggest structural cognitive load.`;
  }
  if (offset < 0) {
    return `Strong recovery pattern detected — high resilience score (${resilienceScore}/100) indicates fast cognitive recovery.`;
  }
  return 'Drift patterns are within normal range — no age adjustment applied.';
}

// ─── Brain Gym Training Rationales ───────────────────────────────────────────

export type CognitiveStateLabel =
  | 'focused' | 'fatigued' | 'stressed' | 'recovering' | 'balanced' | 'elevated';

/**
 * Returns the canonical plan rationale for a given cognitive state.
 * Used in the daily-plan route and brainGymService.
 */
export const PLAN_RATIONALES: Record<CognitiveStateLabel, string> = {
  focused: 'You are in a focused state — optimal for high-challenge training.',
  fatigued: 'Fatigue detected — lighter tasks selected to support recovery.',
  stressed: 'Elevated stress detected — slower-paced training to reduce cognitive load.',
  recovering: 'Recovery phase — moderate tasks to rebuild cognitive capacity.',
  balanced: 'Balanced state — standard adaptive training plan.',
  elevated: 'Elevated performance state — maximising challenge for peak growth.',
};

export function getPlanRationale(state: CognitiveStateLabel): string {
  return PLAN_RATIONALES[state] ?? PLAN_RATIONALES.balanced;
}

/**
 * Returns a task-level rationale string for a training task.
 * Consistent across service and route layers.
 */
export function buildTaskRationale(
  score: number,
  difficulty: 'easy' | 'medium' | 'hard',
  isPlateauDomain: boolean
): string {
  if (isPlateauDomain) {
    return 'Plateau detected — introducing higher challenge to re-stimulate adaptation.';
  }
  if (score < 45) {
    return 'Score is below baseline — starting with lighter load to rebuild confidence.';
  }
  if (score > 75) {
    return 'Strong performance — pushing to higher challenge for continued growth.';
  }
  return `Balanced training at ${difficulty} difficulty based on current cognitive state.`;
}

// ─── Prediction Horizon Explanations ─────────────────────────────────────────

/**
 * Returns the canonical prediction explanation for a given horizon and performance.
 * Consistent across the predictions route and predictionEngineService.
 */
export function buildPredictionExplanation(
  horizonHours: number,
  predictedPerformance: number,
  context?: ExplanationContext
): string {
  const audience = context?.audience ?? 'patient';
  const base = `In ${horizonHours} hour${horizonHours === 1 ? '' : 's'}, your cognitive performance is predicted to be ${predictedPerformance}/100.`;
  if (audience === 'clinician') {
    return `Predicted cognitive performance at T+${horizonHours}h: ${predictedPerformance}/100.`;
  }
  if (context?.confidenceLevel === 'low') {
    return `${base} (This is an estimate — data confidence is currently limited.)`;
  }
  return base;
}

// ─── Unusual Baseline Deviation Messages ─────────────────────────────────────

/**
 * Returns the canonical unusual deviation message.
 * Consistent across human-state service and route.
 */
export function buildDeviationMessage(deviationPct: number): string {
  const sign = deviationPct > 0 ? '+' : '';
  return `This is unusual for you at this time (${sign}${deviationPct.toFixed(0)}% from your norm)`;
}

// ─── Drift Adaptation Messages (Brain Gym) ────────────────────────────────────

/**
 * Returns the canonical adaptation message for drift-driven training adjustments.
 */
export function buildDriftAdaptationMessage(
  driftType: DriftType,
  driftCategory: DriftCategory,
  newDifficulty: 'easy' | 'medium' | 'hard'
): string {
  if (driftType === 'positive') {
    return `Performance improvement detected — training intensity increased to ${newDifficulty}.`;
  }
  if (driftCategory === 'fatigue-related') {
    return `Fatigue signals detected — training adjusted to ${newDifficulty} to support recovery.`;
  }
  if (driftCategory === 'stress-related') {
    return `Stress signals detected — training adjusted to ${newDifficulty} to reduce cognitive load.`;
  }
  return `State change detected — training adjusted to ${newDifficulty} difficulty.`;
}

// ─── Synthetic Scenario Testing ───────────────────────────────────────────────

/**
 * Synthetic test scenario definition.
 */
export interface ExplanationTestScenario {
  name: string;
  description: string;
  params: Parameters<typeof validateExplanationConsistency>[0] & {
    explanationText?: string;
  };
  expectations: {
    shouldBeValid?: boolean;
    shouldHaveViolations?: string[];
    shouldHealExplanation?: boolean;
    shouldNotContain?: string[];
  };
}

/**
 * Result of running a single test scenario.
 */
export interface ExplanationTestResult {
  scenario: string;
  passed: boolean;
  failures: string[];
  validationResult: ConsistencyValidationResult;
}

/**
 * Predefined synthetic scenarios covering edge cases:
 *   - conflicting signals
 *   - extreme drift
 *   - low confidence
 */
export const SYNTHETIC_TEST_SCENARIOS: ExplanationTestScenario[] = [
  {
    name: 'conflicting_positive_drift_fatigued_state',
    description: 'Positive drift but state is Fatigued — should self-heal',
    params: {
      stateLabel: 'Fatigued',
      driftType: 'positive',
      driftCategory: 'recovery-related',
      stabilityScore: 55,
      explanationText:
        'Multiple signals suggest reduced cognitive capacity. Rest or a short break is recommended.',
    },
    expectations: {
      shouldBeValid: false,
      shouldHaveViolations: ['positive drift but state is "Fatigued"'],
      shouldHealExplanation: true,
      shouldNotContain: ['Rest or a short break is recommended'],
    },
  },
  {
    name: 'extreme_negative_drift_focused_state',
    description: 'Negative drift but state is Focused — should add caution note',
    params: {
      stateLabel: 'Focused',
      driftType: 'negative',
      driftCategory: 'fatigue-related',
      stabilityScore: 70,
      explanationText:
        'Your attention, reaction speed, and neural signals indicate a high-performance cognitive state.',
    },
    expectations: {
      shouldBeValid: false,
      shouldHaveViolations: ['negative drift but state is "Focused"'],
      shouldHealExplanation: true,
    },
  },
  {
    name: 'low_confidence_high_prediction',
    description: 'Low confidence with very high predicted performance — should adjust prediction',
    params: {
      stateLabel: 'Balanced',
      confidence: 0.3,
      predictedPerformance: 92,
      explanationText: 'Predicted performance: 92/100.',
    },
    expectations: {
      shouldBeValid: false,
      shouldHaveViolations: ['low confidence'],
      shouldHealExplanation: true,
    },
  },
  {
    name: 'high_stability_overloaded',
    description: 'High stability but Overloaded state — should soften language',
    params: {
      stateLabel: 'Overloaded',
      stabilityScore: 85,
      explanationText:
        'Cognitive load is critically high. Simplify tasks and reduce demands immediately.',
    },
    expectations: {
      shouldBeValid: false,
      shouldHaveViolations: ['high stability score'],
      shouldHealExplanation: true,
      shouldNotContain: ['critically high'],
    },
  },
  {
    name: 'chronic_drift_high_resilience',
    description: 'Chronic drift but high resilience — should add recovery capacity note',
    params: {
      stateLabel: 'Fatigued',
      driftType: 'negative',
      driftClassification: 'chronic',
      resilienceScore: 80,
      explanationText: 'Ongoing decline pattern detected.',
    },
    expectations: {
      shouldBeValid: false,
      shouldHaveViolations: ['high resilience score'],
      shouldHealExplanation: true,
    },
  },
  {
    name: 'all_signals_aligned_high_confidence',
    description: 'All signals aligned — should be valid with no violations',
    params: {
      stateLabel: 'Focused',
      driftType: 'positive',
      driftCategory: 'performance-improvement',
      driftClassification: 'none',
      stabilityScore: 85,
      resilienceScore: 80,
      confidence: 0.9,
      predictedPerformance: 78,
      horizonHours: 2,
    },
    expectations: {
      shouldBeValid: true,
    },
  },
  {
    name: 'performance_improvement_drift_fatigued',
    description: 'Performance improvement drift but Fatigued state — should soften fatigue',
    params: {
      stateLabel: 'Fatigued',
      driftType: 'positive',
      driftCategory: 'performance-improvement',
      explanationText:
        'Multiple signals suggest reduced cognitive capacity. Rest or a short break is recommended.',
    },
    expectations: {
      shouldBeValid: false,
      shouldHealExplanation: true,
      shouldNotContain: ['Rest or a short break is recommended'],
    },
  },
];

/**
 * Runs a single synthetic test scenario and returns the result.
 */
export function runExplanationTestScenario(
  scenario: ExplanationTestScenario
): ExplanationTestResult {
  const { params, expectations } = scenario;
  const failures: string[] = [];

  let result = validateExplanationConsistency(params);

  // Check validity expectation
  if (expectations.shouldBeValid !== undefined) {
    if (expectations.shouldBeValid && !result.valid) {
      failures.push(
        `Expected valid but got violations: ${result.violations.join('; ')}`
      );
    }
    if (!expectations.shouldBeValid && result.valid) {
      failures.push('Expected violations but explanation was valid');
    }
  }

  // Check specific violations
  if (expectations.shouldHaveViolations) {
    for (const expectedViolation of expectations.shouldHaveViolations) {
      const found = result.violations.some((v) =>
        v.toLowerCase().includes(expectedViolation.toLowerCase())
      );
      if (!found) {
        failures.push(`Expected violation containing "${expectedViolation}" but not found`);
      }
    }
  }

  // Check self-healing
  if (expectations.shouldHealExplanation) {
    if (!result.healedExplanation) {
      failures.push('Expected self-healing to produce a healed explanation but none was returned');
    }
  }

  // Check that healed explanation does not contain forbidden strings
  if (expectations.shouldNotContain && result.healedExplanation) {
    for (const forbidden of expectations.shouldNotContain) {
      if (result.healedExplanation.includes(forbidden)) {
        failures.push(
          `Healed explanation should not contain "${forbidden}" but it does`
        );
      }
    }
  }

  return {
    scenario: scenario.name,
    passed: failures.length === 0,
    failures,
    validationResult: result,
  };
}

/**
 * Runs all synthetic test scenarios and returns a summary report.
 * Use this to validate the robustness of the explanation system under edge cases.
 */
export function runAllExplanationTests(): {
  totalScenarios: number;
  passed: number;
  failed: number;
  results: ExplanationTestResult[];
  allPassed: boolean;
} {
  const results = SYNTHETIC_TEST_SCENARIOS.map(runExplanationTestScenario);
  const passed = results.filter((r) => r.passed).length;
  const failed = results.filter((r) => !r.passed).length;

  return {
    totalScenarios: results.length,
    passed,
    failed,
    results,
    allPassed: failed === 0,
  };
}
